/*! Cling Fishing engraving customizer v3
 * One product per page (the product page it sits on). Customers pick a color, design one or more
 * engraving areas, approve a proof and add to cart. Styling is inherited from the store theme.
 * Usage: ClingCustomizer.mount(element, config). See README for the config shape.
 */
(function (global) {
  'use strict';
  const E = global.ClingEngrave;
  // STAGE is the preview width; its height fits each product.
  // Laser files are made at DPI (1000: crisp lines, fine detail, and a detailed traced SVG). Photos use PHOTO_DPI,
  // because their dot pattern needs dots large enough for the laser to mark cleanly.
  const STAGE = 800, PAD = 36, DPI = 1000, PHOTO_DPI = 333;

  /* Measured from the "for customizing" renders (cropped, background removed).
     aspect   render width / height
     bodyX    where the anodized body starts and ends across the render, and bodyY top to bottom
     bodyY    (fractions, measured from the renders; key rings, felt and shadows excluded).
     widthIn  real outside dimensions of the anodized body, in inches (from Clingfishing):
     heightIn Mini 1.5 x 1.5, Doublestack 3 x 1.5, Plus 2.5 x 2.5, Delta 6 x 3, Boat Bar 11.75 x 4.
              Width sets the horizontal scale and height the vertical one, so every engraving area's
              real size (and its laser file) follows from where it sits on the render.
     views    sides of the product the preview can show (each uses the same render)
     areas    engraving areas: face = x, y, w, h as fractions of the render; r = corner radius
              as a fraction of the area's short side. Areas are listed in the order customers see them. */
  // Build an area from an outline traced off the render (points as render fractions).
  function shapedArea(key, label, pts, round) {
    const xs = pts.map((q) => q[0]), ys = pts.map((q) => q[1]);
    const face = { x: Math.min(...xs), y: Math.min(...ys), w: Math.max(...xs) - Math.min(...xs), h: Math.max(...ys) - Math.min(...ys), r: round };
    return { key, label, view: 'front', face, poly: pts.map(([x, y, r]) => [(x - face.x) / face.w, (y - face.y) / face.h, r]) };
  }
  // Boat Bar pads, traced from the render: each outline was found by scanning outward from the pad's
  // center to its edge line (dark on the shaded sides, bright on the lit sides) in four colors, then
  // fitted with straight edges. The dashed guide sits on that edge; engraving stays MARGIN_IN inside.
  // Side pads: angled corner by the top screw hole, rounded corner by the bottom one, and an outer
  // side that tapers in near the ends to follow the bar.
  const BB_LEFT = [[0.0529, 0.33], [0.072, 0.2], [0.098, 0.156], [0.2392, 0.156], [0.3074, 0.3536], [0.3074, 0.8],
    [0.284, 0.8488], [0.098, 0.8488], [0.074, 0.81], [0.0529, 0.65]];
  const BB_RIGHT = [[0.9473, 0.6477], [0.9263, 0.8083], [0.9025, 0.8473], [0.7178, 0.8473], [0.6946, 0.7983], [0.6946, 0.3311],
    [0.766, 0.1518], [0.9025, 0.1518], [0.9283, 0.196], [0.9473, 0.3265]];
  const MARGIN_IN = 0.03; // engraving stays this far inside every area outline
  // Areas marked this way offer built-in Cling artwork from one group of the art library:
  //   'fish'  single fish (Largemouth, Hook Jaw) for side panels; `facing` turns them toward the center
  //   'scene' full-panel scenes made for the Boat Bar's center panel
  const withArt = (a, facing, group = 'fish') => Object.assign(a, { art: group, facing });

  const RENDERS = {
    'delta': {
      aspect: 1100 / 576, bodyX: [0.0291, 0.97], bodyY: [0.0729, 0.941], widthIn: 6.0, heightIn: 3.0,
      views: [{ key: 'front', label: 'Front' }],
      // Left and right panels, measured from the render across four colors: the edge of each pocket's flat
      // floor. Corner positions and radii were fitted to the traced floor edge (median error under 1 px). Each has a chamfer at its top inner corner (beside the textured center strip), an outer side
      // that angles in toward the ends of the bar, and its own measured radius on every corner
      // (third value, as a fraction of the render's width).
      areas: [
        withArt(shapedArea('left', 'Left panel', [[0.106, 0.176, 0.0212], [0.2888, 0.1657, 0.0109], [0.3807, 0.3255, 0.0201], [0.3773, 0.861, 0.0231], [0.0985, 0.8636, 0.0467], [0.0714, 0.6943, 0.0818], [0.0749, 0.3122, 0.0818]], 0.04), 'right'),
        withArt(shapedArea('right', 'Right panel', [[0.714, 0.1624, 0.0818], [0.8949, 0.1699, 0.0366], [0.9337, 0.2979, 0.064], [0.9261, 0.7498, 0.0218], [0.9086, 0.8585, 0.0387], [0.6272, 0.8705, 0.0273], [0.6273, 0.3183, 0.0479]], 0.04), 'left'),
      ],
    },
    'doublestack': {
      aspect: 1076 / 562, bodyX: [0.0334, 0.9676], bodyY: [0.0569, 0.9324], widthIn: 3.0, heightIn: 1.5,
      views: [{ key: 'front', label: 'Front' }],
      // One engraving area: the inside flat of the tray, traced where the floor meets the wall
      // (consistent across all colors): x 0.1028–0.897, y 0.184–0.803, corner radius about 50 px on the render.
      areas: [
        { key: 'front', label: 'Front', view: 'front', face: { x: 0.1028, y: 0.184, w: 0.7942, h: 0.619, r: 0.145 } },
      ],
    },
    'boat-bar': {
      aspect: 1100 / 413, bodyX: [0.03, 0.97], bodyY: [0.0799, 0.9201], widthIn: 11.75, heightIn: 4.0,
      views: [{ key: 'front', label: 'Front' }],
      areas: [
        // Center pad edge: x 0.3328–0.6696, y 0.354–0.8419, corner radius about 22 px on the render.
        withArt({ key: 'center', label: 'Center panel', view: 'front', face: { x: 0.3328, y: 0.354, w: 0.3368, h: 0.4879, r: 0.11 } }, null, 'scene'),
        withArt(shapedArea('left', 'Left panel', BB_LEFT, 0.05), 'right'),
        withArt(shapedArea('right', 'Right panel', BB_RIGHT, 0.05), 'left'),
      ],
    },
    'mini': {
      aspect: 981 / 1055, bodyX: [0.0947, 0.9664], bodyY: [0.1318, 0.9673], widthIn: 1.5, heightIn: 1.5,
      views: [{ key: 'front', label: 'Front' }],
      // Inside flat, traced where the floor meets the wall (consistent across all four colors).
      areas: [{ key: 'face', label: 'Face', view: 'front', face: { x: 0.2197, y: 0.2463, w: 0.6262, h: 0.5979, r: 0.139 } }],
    },
    'plus': {
      aspect: 1072 / 1100, bodyX: [0.0569, 0.9683], bodyY: [0.0782, 0.9573], widthIn: 2.5, heightIn: 2.5,
      views: [{ key: 'front', label: 'Front' }],
      // Inside flat, traced where the floor meets the wall (consistent across all four colors).
      areas: [{ key: 'face', label: 'Face', view: 'front', face: { x: 0.1824, y: 0.1968, w: 0.6611, h: 0.6465, r: 0.096 } }],
    },
  };

  // Order of the "What should we engrave?" buttons: Logo, Text, then any stock artwork, then Blank.
  // (Monogram is no longer offered; the engine still supports it if you want it back.)
  const TYPES = [{ id: 'logo', label: 'Logo' }, { id: 'text', label: 'Text' }];
  const BLANK = { id: 'none', label: 'Blank' };
  const TYPE_LABEL = { art: 'Artwork', logo: 'Logo', text: 'Text', mono: 'Monogram', none: 'Blank', onfile: 'Artwork on file' };
  const MAX_FILE = 20 * 1024 * 1024;

  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const fmtIn = (n) => String(+(+n).toFixed(2));
  function hexToRgb(hex) {
    let h = String(hex || '#888').replace('#', '');
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    const n = parseInt(h, 16) || 0;
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }
  function lum(hex) {
    const [r, g, b] = hexToRgb(hex).map((v) => { v /= 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  }
  function rrPath(ctx, x, y, w, h, r) {
    r = Math.max(0, Math.min(r, w / 2, h / 2));
    ctx.beginPath(); ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r); ctx.closePath();
  }

  const IMG = new Map();
  function loadImg(url, fallback) {
    if (!url) return Promise.resolve(fallback ? loadImg(fallback) : null);
    if (IMG.has(url)) return IMG.get(url);
    const p = new Promise((res) => {
      const i = new Image();
      i.crossOrigin = 'anonymous';
      i.decoding = 'async';
      i.onload = () => res(i);
      i.onerror = () => res(fallback ? loadImg(fallback) : null);
      i.src = url;
    });
    IMG.set(url, p);
    return p;
  }

  function blankDesign() {
    return {
      type: 'none', src: null, file: null, fileName: '', mode: 'line', threshold: 128, invert: false, bright: 0,
      // position (fraction of area), size, rotation (degrees)
      cx: 0.5, cy: 0.5, scale: 0.8, rot: 0, flip: false, artId: '', rights: false, lines: ['', '', ''], font: 'Oswald',
      mono: { first: '', middle: '', last: '' }, monoStyle: 'classic',
      note: '', error: '', _cache: null, _rev: 0,
    };
  }

  /* ======================================================================== */
  function mount(root, cfg) {
    cfg = Object.assign({ mode: 'demo', currency: 'USD', buttonClass: '', secondaryButtonClass: '', headingClass: '' }, cfg);
    E.configure({ convertUrl: cfg.convertUrl, pdfjsUrl: cfg.pdfjsUrl, pdfjsWorkerUrl: cfg.pdfjsWorkerUrl });
    const p = cfg.product;
    if (!p || !p.variants || !p.variants.length) { root.innerHTML = '<p>This product is not set up for engraving yet.</p>'; return; }
    const R = RENDERS[p.render] || RENDERS.delta;
    p.aspect = R.aspect;
    p.widthIn = +p.widthIn || R.widthIn;
    p.heightIn = +p.heightIn || R.heightIn;
    p.views = R.views;
    p.areas = (p.areas || R.areas).map((a) => Object.assign({}, a));
    const hexMap = parseHexMap(cfg.colorHex);
    p.variants.forEach((v) => { v.hex = v.hex || hexMap[(v.color || '').toLowerCase()] || '#555555'; });
    p.extraAreaPrice = +p.extraAreaPrice || 0;   // cents per engraved area beyond the first

    const priced = p.variants.every((v) => typeof v.price === 'number');
    // Bulk discount tiers (quantity -> % off). The real discount is applied by Shopify at checkout; this only
    // shows customers where they stand. Accepts "10:20, 25:30, 50:40" (theme setting) or [[10,20], ...].
    // Dealers (logged in for a B2B company): their catalog's prices, no volume pricing, and a minimum number of
    // customized units per order, all designs combined (set in snippets/cling-everywhere.liquid)
    const CZ = global.ClingCustomizable || {};
    // Assisted orders: staff (sales reps, and order builders picked on the proof dashboard), logged in to the store with
    // their staff email, order for a B2B company or (order builders) any customer right here. Each approved design goes
    // into that company's or customer's order (a Shopify draft order) instead of the cart. The proof server decides
    // who's staff and what they may order for (loadRepMode below). A target is "L<company location>" or "C<customer>".
    let REP = false;
    const RS = { me: null, target: '', label: '', contact: '', ctx: null, order: null, rates: null, invoice: false, busy: false, note: '' };
    let DEALER = !!CZ.dealer, DEALER_MIN = DEALER ? (parseInt(CZ.dealerMin, 10) || 20) : 0;
    // Dealers can engrave an area with the artwork their company has on file instead of uploading it again
    const ONFILE_LABEL = ['ARTWORK', 'ON FILE'];
    const BASE_TIERS = (Array.isArray(cfg.bulkTiers) ? cfg.bulkTiers
      : String(cfg.bulkTiers || '').split(/[\n,]+/).map((t) => t.split(/[:=]/).map((v) => parseFloat(v))))
      .filter((t) => t.length === 2 && t[0] > 0 && t[1] > 0 && t[1] < 100).sort((a, b) => a[0] - b[0]);
    let TIERS = DEALER ? [] : BASE_TIERS; // dealers (and staff ordering for a company) have no volume tiers
    const money = (c) => new Intl.NumberFormat(undefined, { style: 'currency', currency: cfg.currency }).format(c / 100);
    const dpr = Math.min(2, global.devicePixelRatio || 1);
    const btn = (extra) => `cc-btn ${cfg.buttonClass ? cfg.buttonClass + ' cc-themed' : ''} ${extra || ''}`;
    const btn2 = (extra) => `cc-btn cc-btn-quiet ${cfg.secondaryButtonClass ? cfg.secondaryButtonClass + ' cc-themed' : ''} ${extra || ''}`;
    const multi = p.areas.length > 1;
    // Built-in artwork. Items whose image fails to load (e.g. not uploaded yet) are left out automatically.
    const ART = (cfg.artLibrary || []).filter((a) => a && a.url).map((a) => Object.assign({ src: null, failed: false }, a));
    const artReady = (group) => ART.filter((a) => a.src && (!group || (a.group || 'fish') === group));
    const artOf = (id) => ART.find((a) => a.id === id && a.src);
    const offersArt = (a) => !!a.art && artReady(a.art).length > 0;
    // Panel-fitted art (scenes) is sized and centered to fill its panel exactly, with no handles.
    const isFitted = (d) => d.type === 'art' && artOf(d.artId) && artOf(d.artId).fit === 'panel';
    // Small groups get one button per piece; bigger ones get a single Artwork button and a gallery.
    const galleryFor = (a) => offersArt(a) && artReady(a.art).length > 2;
    // Art files say which way the fish faces as drawn; flip it when the panel wants the other way.
    const defaultFlip = (artId, area) => { const it = artOf(artId); return !!(it && area.facing && (it.faces || 'left') !== area.facing); };
    const artFacing = (d) => { const it = artOf(d.artId); const native = (it && it.faces) || 'left'; return d.flip ? (native === 'left' ? 'right' : 'left') : native; };
    const inPerUnit = p.widthIn / (R.bodyX[1] - R.bodyX[0]); // inches per render width
    const inPerUnitY = p.heightIn / (R.bodyY[1] - R.bodyY[0]); // inches per render height
    // Wide products get a shorter preview so they aren't lost in empty space.
    const SH = Math.round(Math.min(STAGE, (STAGE - PAD * 2) / p.aspect + PAD * 2 + 48));

    const S = {
      step: 1, maxStep: 1, v: initialVariant(), qty: 1, qtyBy: {}, layer: 'main', sheet: null,
      area: p.areas[0].key, designs: {}, notes: '', approved: false, stepError: '', busy: false,
    };
    p.areas.forEach((a) => (S.designs[a.key] = blankDesign()));
    S.designs[p.areas[0].key].type = 'logo';

    root.classList.add('cc');
    root.innerHTML = `
      <div class="cc-stage-wrap">
        <canvas class="cc-stage" tabindex="0" width="${STAGE * dpr}" height="${SH * dpr}" style="--ar:${STAGE / SH}" role="img" aria-label="Preview of your engraved ${esc(p.title)}"></canvas>
        <div class="cc-view" role="group" aria-label="Preview side"></div>
        <p class="cc-stage-note"></p>
        <div class="cc-mtools" data-mtools></div>
      </div>
      <div class="cc-panel">
        ${cfg.backUrl ? `<a class="cc-back" href="${esc(cfg.backUrl)}">${esc(cfg.backLabel || 'All customizable products')}</a>` : ''}
        <h1 class="cc-title ${esc(cfg.titleClass || '')}">${esc(p.title)}</h1>
        <p class="cc-price"></p>
        <ol class="cc-steps"></ol>
        ${cfg.tour === false ? '' : '<button type="button" class="cc-link cc-tour-again" data-act="tour">How it works</button>'}
        <div class="cc-body" aria-live="polite"></div>
        <p class="cc-step-error" role="alert"></p>
        <div class="cc-nav"></div>
      </div>
      <dialog class="cc-dialog"></dialog>
      <dialog class="cc-adjust" aria-labelledby="cc-adjust-title"></dialog>
      <div class="cc-sheet-backdrop" data-act="sheet-close" hidden></div>
      <div class="cc-sheet" role="dialog" aria-modal="true" aria-labelledby="cc-sheet-title" hidden></div>
      <div class="cc-tour-ring" hidden></div>
      <div class="cc-tour" role="dialog" aria-modal="false" aria-labelledby="cc-tour-title" hidden></div>`;
    const stage = root.querySelector('.cc-stage');
    const sctx = stage.getContext('2d');
    const $ = (sel) => root.querySelector(sel);

    /* ---------- state ---------- */
    function initialVariant() {
      const want = new URLSearchParams(global.location.search).get('variant');
      const i = p.variants.findIndex((v) => String(v.id) === want);
      if (i >= 0) return i;
      const a = p.variants.findIndex((v) => v.available !== false);
      return a < 0 ? 0 : a;
    }
    const variant = () => p.variants[S.v];
    const areaOf = (k) => p.areas.find((a) => a.key === k);
    const cur = () => S.designs[S.area];
    const curArea = () => areaOf(S.area);
    const view = () => curArea().view;
    // An area counts once it has something to engrave: a logo area with no file yet doesn't block or cost anything.
    const isEngraved = (k) => {
      const d = S.designs[k];
      if (d.type === 'none') return false;
      if (d.type === 'logo') return !!d.src;
      if (d.type === 'art') return !!artOf(d.artId);
      return true; // text, monogram, artwork on file (dealers)
    };
    const engraved = () => p.areas.filter((a) => isEngraved(a.key)).map((a) => a.key);
    const extraCount = () => Math.max(0, engraved().length - 1);
    const unitPrice = () => (priced ? variant().price : 0) + p.extraAreaPrice * extraCount();
    // Quantities per color (variant index -> units). One design can be ordered in several colors; every unit
    // counts toward the bulk discount.
    const qtyMap = () => (Object.values(S.qtyBy).some((n) => n > 0) ? S.qtyBy : { [S.v]: Math.max(1, S.qty) });
    const totalQty = () => Object.values(qtyMap()).reduce((a, n) => a + n, 0);
    const chosen = () => Object.entries(qtyMap()).filter(([, n]) => n > 0).map(([i, n]) => ({ i: +i, n, v: p.variants[+i] }));
    const primaryIndex = () => ((qtyMap()[S.v] || 0) > 0 ? S.v : chosen()[0].i);
    const total = () => (priced ? chosen().reduce((a, c) => a + c.v.price * c.n, 0) : 0) + p.extraAreaPrice * extraCount() * totalQty();
    const areaIn = (a) => ({ w: a.face.w * inPerUnit, h: a.face.h * inPerUnitY });

    function layout() {
      const availW = STAGE - PAD * 2, availH = SH - PAD * 2;
      let w = availW, h = availW / p.aspect;
      if (h > availH) { h = availH; w = availH * p.aspect; }
      return { x: (STAGE - w) / 2, y: (SH - h) / 2, w, h };
    }
    function faceRect(a) {
      const L = layout(), f = a.face;
      const r = { x: L.x + f.x * L.w, y: L.y + f.y * L.h, w: f.w * L.w, h: f.h * L.h };
      r.r = f.r * Math.min(r.w, r.h);
      return r;
    }
    function designAspect(d) {
      if (d.type === 'logo' && d.src) return d.src.canvas.width / d.src.canvas.height;
      if (d.type === 'art' && artOf(d.artId)) { const c = artOf(d.artId).src.canvas; return c.width / c.height; }
      if (d.type === 'text') return E.textAspect(d.lines, d.font);
      if (d.type === 'mono') return E.monoAspect(d.mono, d.monoStyle);
      if (d.type === 'onfile') return E.textAspect(ONFILE_LABEL, 'Oswald');
      return 1;
    }
    // The artwork's box inside a W×H area: centered at (cx, cy), fit to `scale` of the area, rotated.
    function designBox(d, W, H) {
      if (isFitted(d)) {
        // Fill the panel at full size, perfectly centered, keeping the art's proportions.
        const aspect = designAspect(d); let w = W, h = w / aspect;
        if (h > H) { h = H; w = h * aspect; }
        return { x: (W - w) / 2, y: (H - h) / 2, w, h, rot: 0, flip: false };
      }
      const aspect = designAspect(d), sc = d.scale;
      let w = W * sc, h = w / aspect;
      if (h > H * sc) { h = H * sc; w = h * aspect; }
      return { x: d.cx * W - w / 2, y: d.cy * H - h / 2, w, h, rot: (d.rot * Math.PI) / 180, flip: !!d.flip && (d.type === 'logo' || d.type === 'art') };
    }
    /* Text added alongside a logo or fish art: a separate piece with its own position, size and rotation.
       S.layer says which piece the handles, keyboard and sliders are adjusting ('main' or 'text'). */
    const textCapable = (d) => (d.type === 'logo' && !!d.src) || (d.type === 'art' && !!artOf(d.artId) && !isFitted(d));
    const hasTextLayer = (d) => textCapable(d) && !!d.textLayer;
    const layerOf = (d) => (S.layer === 'text' && hasTextLayer(d) ? 'text' : 'main');
    const target = () => { const d = cur(); return layerOf(d) === 'text' ? d.textLayer : d; };
    const textObj = (t) => ({ type: 'text', lines: t.lines, font: t.font, cx: t.cx, cy: t.cy, scale: t.scale, rot: t.rot, flip: false });
    const textLines = (t) => !!t && t.lines.some((l) => l.trim());

    // Trace an area's outline (rounded rect, or a traced pad shape) into a box at x, y, w, h.
    function areaPath(ctx, a, x, y, w, h) {
      const r = a.face.r * Math.min(w, h);
      if (!a.poly) return rrPath(ctx, x, y, w, h, r);
      // a corner can carry its own radius (fraction of the render's width); otherwise the area's default
      const perPx = w / a.face.w;
      const P = a.poly.map(([u, v, rr]) => [x + u * w, y + v * h, rr != null ? rr * perPx : r]), n = P.length;
      ctx.beginPath();
      const mid = (i, j) => [(P[i][0] + P[j][0]) / 2, (P[i][1] + P[j][1]) / 2];
      const m0 = mid(n - 1, 0);
      ctx.moveTo(m0[0], m0[1]);
      for (let i = 0; i < n; i++) { const nx = P[(i + 1) % n]; ctx.arcTo(P[i][0], P[i][1], nx[0], nx[1], P[i][2]); }
      ctx.closePath();
    }
    // Nothing engraves outside an area's outline, in the preview or the production file.
    function clipToArea(mask, W, H, a) {
      const c = E.makeCanvas(W, H), x = c.getContext('2d');
      areaPath(x, a, 0, 0, W, H); x.fillStyle = '#000'; x.fill();
      // Keep a small physical margin inside the outline, the same at every resolution.
      const marginPx = (MARGIN_IN * W) / areaIn(a).w;
      if (marginPx > 0.5) {
        x.globalCompositeOperation = 'destination-out';
        x.lineWidth = marginPx * 2; x.lineJoin = 'round';
        x.stroke();
      }
      const d = x.getImageData(0, 0, W, H).data;
      for (let i = 0; i < mask.length; i++) if (d[i * 4 + 3] < 128) mask[i] = 0;
    }
    // Physical width of the gap between touching colors, in inches (about 0.3 mm, a clean laser line).
    const COLOR_GAP_IN = 0.012;
    // A customer's logo converted to white engraving: chosen colors white, background and holes clear.
    function whiteLogo(d, a, r, W) {
      const lk = d.bgTol + '~' + d._rev + '~' + (d.lineShift || 0);
      if (!d._lbl || d._lbl.key !== lk) { d.an.lineShift = d.lineShift || 0; d._lbl = { key: lk, L: E.labelLogo(d.src.canvas, d.an, d.bgTol) }; }
      const L = d._lbl.L;
      // the gap is a fixed physical width, so convert it to source pixels at the current size
      const designWIn = (r.w / W) * areaIn(a).w;
      const gapR = d.gap && d.colorsOn.filter(Boolean).length > 1 ? Math.max(1, Math.round(((COLOR_GAP_IN / 2) * L.W) / Math.max(0.05, designWIn))) : 0;
      const key = [d._lbl.key, d.colorsOn.join(''), gapR].join('|');
      // traced once per setting change into smooth vector outlines, so it draws crisply at any size
      if (!d._white || d._white.key !== key) d._white = { key, V: E.traceMask(E.logoMask(L, d.colorsOn, gapR), L.W, L.H) };
      return d._white.V;
    }
    // The converted logo drawn from its outlines at an exact pixel size (white on clear).
    function whiteCanvas(V, w, h) {
      w = Math.max(1, Math.ceil(w)); h = Math.max(1, Math.ceil(h));
      const c = E.makeCanvas(w, h);
      E.fillOutlines(c.getContext('2d'), V, 0, 0, w, h);
      return c;
    }
    // A newly uploaded logo starts at a size where none of it is trimmed by the engraving area's shape (rounded
    // corners, angled panels): if any of its white would fall outside, it's scaled down a step at a time.
    // Logos that already fit keep the usual size; customers can still enlarge it with the size slider.
    function fitLogoInside(areaKey) {
      const d = S.designs[areaKey], a = areaOf(areaKey);
      if (!d || d.type !== 'logo' || !d.src) return;
      const f = faceRect(a), W = 360, H = Math.max(40, Math.round((W * f.h) / f.w));
      const count = (m) => { let n = 0; for (let i = 0; i < m.length; i++) n += m[i] ? 1 : 0; return n; };
      for (let step = 0; step < 16; step++) {
        const r = designBox(d, W, H);
        const m = d.mode === 'photo'
          ? E.maskFromSource(d.src.canvas, W, H, r, { mode: 'photo', invert: d.invert, bright: d.bright })
          : E.maskFromSource(whiteCanvas(whiteLogo(d, a, r, W), r.w, r.h), W, H, r, { mode: 'line', threshold: 128, invert: true, bright: 0 });
        const all = count(m); if (!all) return;
        const kept = m.slice(); clipToArea(kept, W, H, a);
        if (all - count(kept) <= all * 0.002) break;   // nothing meaningful trimmed
        d.scale = Math.max(0.2, d.scale * 0.95);
      }
      d._cache = null;
    }
    function maskFor(areaKey, W, H) {
      const d = S.designs[areaKey], a = areaOf(areaKey);
      if (d.type === 'none') return null;
      const key = [JSON.stringify(hasTextLayer(d) ? d.textLayer : null), d.type, W, H, d._rev, d.artId, d.flip, d.mode, d.threshold, d.invert, d.bright, d.bgTol, d.lineShift, (d.colorsOn || []).join(''), d.gap, d.cx, d.cy, d.scale, d.rot,
        d.lines.join('|'), d.font, d.mono.first, d.mono.middle, d.mono.last, d.monoStyle].join('~');
      if (d._cache && d._cache.key === key) return d._cache.val;
      let val = null;
      const r = designBox(d, W, H);
      if (d.type === 'onfile') {
        // a placeholder label on the preview and proof; the laser file is made from the dealer's artwork on file
        const o = E.maskFromText(ONFILE_LABEL, 'Oswald', W, H, r);
        val = { rect: r, mask: o.mask, fontPx: o.fontPx };
      }
      if (d.type === 'logo' && d.src) {
        val = d.mode === 'photo'
          ? { rect: r, mask: E.maskFromSource(d.src.canvas, W, H, r, { mode: 'photo', invert: d.invert, bright: d.bright }) }
          : { rect: r, mask: E.maskFromSource(whiteCanvas(whiteLogo(d, a, r, W), r.w, r.h), W, H, r, { mode: 'line', threshold: 128, invert: true, bright: 0 }) };
      } else if (d.type === 'art' && artOf(d.artId)) {
        // Built-in art is light line work on a transparent background: engrave the lines.
        val = { rect: r, mask: E.maskFromSource(artOf(d.artId).src.canvas, W, H, r, { mode: 'line', threshold: 128, invert: true, bright: 0 }) };
      } else if (d.type === 'text') {
        const o = E.maskFromText(d.lines, d.font, W, H, r);
        val = { rect: r, mask: o.mask, fontPx: o.fontPx };
      } else if (d.type === 'mono') {
        const o = E.maskFromMonogram(d.mono, d.monoStyle, W, H, r);
        val = { rect: r, mask: o.mask, fontPx: o.fontPx };
      }
      if (val && hasTextLayer(d) && textLines(d.textLayer)) {
        const tr = designBox(textObj(d.textLayer), W, H), o = E.maskFromText(d.textLayer.lines, d.textLayer.font, W, H, tr);
        for (let i = 0; i < o.mask.length; i++) if (o.mask[i]) val.mask[i] = 1;
        val.textFontPx = o.fontPx;
      }
      if (val) { clipToArea(val.mask, W, H, a); val.W = W; val.H = H; val.coverage = E.coverage(val.mask, W, H, val.rect); }
      d._cache = { key, val };
      return val;
    }
    // Preview sharpness: engraving is computed at SUPERSAMPLE x the on-screen pixel size, then smoothed down,
    // so fine line work has clean anti-aliased edges instead of jagged pixels. While dragging, a lighter
    // pass keeps movement smooth; full quality is redrawn as soon as the drag ends.
    const SUPERSAMPLE = 3, DRAG_SAMPLE = 1.25, MAX_MASK_PX = 4e6;
    let dragging = false;
    function previewDims(f, scale) {
      const k = scale * (dragging ? DRAG_SAMPLE : SUPERSAMPLE);
      let w = f.w * k, h = f.h * k;
      const over = Math.sqrt((w * h) / MAX_MASK_PX);
      if (over > 1) { w /= over; h /= over; }
      return [Math.max(1, Math.round(w)), Math.max(1, Math.round(h))];
    }
    // Halve repeatedly toward the target size: each step averages neighbors, giving smooth edges.
    function smoothDown(src, tw, th) {
      let c = src;
      while (c.width / 2 >= tw && c.height / 2 >= th) {
        const n = E.makeCanvas(Math.round(c.width / 2), Math.round(c.height / 2)), x = n.getContext('2d');
        x.imageSmoothingEnabled = true; x.imageSmoothingQuality = 'high';
        x.drawImage(c, 0, 0, n.width, n.height); c = n;
      }
      return c;
    }
    const stageMask = (k) => { const f = faceRect(areaOf(k)); const [w, h] = previewDims(f, dpr); return maskFor(k, w, h); };

    /* ---------- drawing ---------- */
    let drawToken = 0;
    function paintScene(ctx, viewKey, scale, guides, image, onlyArea, vv) {
      const v = vv || variant(), L = layout();
      ctx.clearRect(0, 0, STAGE, SH);
      if (image) { ctx.imageSmoothingQuality = 'high'; ctx.drawImage(image, L.x, L.y, L.w, L.h); }
      else { ctx.save(); ctx.fillStyle = 'rgba(128,128,128,.12)'; rrPath(ctx, L.x, L.y, L.w, L.h, 8); ctx.fill(); ctx.restore(); }
      const tone = lum(v.hex) > 0.5 ? [196, 199, 201, 235] : [250, 250, 248, 240];
      const light = lum(v.hex) > 0.35;
      p.areas.filter((a) => a.view === viewKey && (!onlyArea || a.key === onlyArea)).forEach((a) => {
        const f = faceRect(a);
        const [mw, mh] = previewDims(f, scale);
        const res = maskFor(a.key, mw, mh);
        if (res) {
          const layer = smoothDown(E.paint(res.mask, mw, mh, tone), Math.round(f.w * scale), Math.round(f.h * scale));
          ctx.save(); ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(layer, f.x, f.y, f.w, f.h);
          ctx.restore();
        }
        if (guides) {
          const active = a.key === S.area;
          ctx.save();
          ctx.setLineDash(active ? [7, 6] : [3, 6]);
          ctx.lineWidth = active ? 2 : 1.25;
          ctx.strokeStyle = light ? `rgba(20,20,20,${active ? 0.7 : 0.35})` : `rgba(255,255,255,${active ? 0.85 : 0.4})`;
          areaPath(ctx, a, f.x, f.y, f.w, f.h); ctx.stroke();
          ctx.restore();
        }
      });
      if (guides) drawHandles(ctx, viewKey);
    }

    /* ---------- direct manipulation on the preview ---------- */
    // Stage units per CSS pixel, so handles stay the same on-screen size at any preview width.
    const unit = () => STAGE / (stage.getBoundingClientRect().width || STAGE);
    // The active design's box in stage coordinates, or null when there's nothing to grab.
    function activeBox() {
      // artwork on file: a fixed placeholder (our team places the artwork), so no handles and no dragging
      if (cur().type === 'onfile') return null;
      return pieceBox(layerOf(cur()));
    }
    // Box of one piece ('main' or 'text') of the current area's design, in stage coordinates.
    function pieceBox(which) {
      const a = curArea(), d = cur();
      if (S.step !== 1 || !isEngraved(S.area) || isFitted(d)) return null;
      if (which === 'text' && !hasTextLayer(d)) return null;
      const f = faceRect(a), b = designBox(which === 'text' ? textObj(d.textLayer) : d, f.w, f.h);
      return { cx: f.x + b.x + b.w / 2, cy: f.y + b.y + b.h / 2, w: b.w, h: b.h, rot: b.rot, f, view: a.view, which };
    }
    function inBox(B, pt, pad) {
      const dx = pt.x - B.cx, dy = pt.y - B.cy, c = Math.cos(-B.rot), s2 = Math.sin(-B.rot);
      return Math.abs(dx * c - dy * s2) <= B.w / 2 + pad && Math.abs(dx * s2 + dy * c) <= B.h / 2 + pad;
    }
    function handlePoints(B) {
      const k = unit(), cos = Math.cos(B.rot), sin = Math.sin(B.rot);
      const at = (lx, ly) => ({ x: B.cx + lx * cos - ly * sin, y: B.cy + lx * sin + ly * cos });
      const hw = B.w / 2, hh = B.h / 2;
      return {
        corners: [at(-hw, -hh), at(hw, -hh), at(hw, hh), at(-hw, hh)],
        rotate: at(0, -hh - 30 * k), stem: at(0, -hh),
      };
    }
    function drawHandles(ctx, viewKey) {
      const B = activeBox();
      if (!B || B.view !== viewKey) return;
      const k = unit(), H = handlePoints(B);
      // the other piece (logo or text) gets a faint dotted outline, so it's clear it can be selected too
      const O = pieceBox(B.which === 'text' ? 'main' : 'text');
      if (O) {
        const oc = handlePoints(O).corners;
        ctx.save(); ctx.setLineDash([3 * k, 4 * k]); ctx.lineWidth = 1.25 * k; ctx.strokeStyle = 'rgba(255,255,255,.75)';
        ctx.beginPath(); oc.forEach((q, i) => (i ? ctx.lineTo(q.x, q.y) : ctx.moveTo(q.x, q.y))); ctx.closePath(); ctx.stroke(); ctx.restore();
      }
      const stroke = (w, c) => { ctx.lineWidth = w * k; ctx.strokeStyle = c; ctx.stroke(); };
      ctx.save();
      // Box outline: a dark line under a light one, visible on every finish.
      ctx.beginPath(); H.corners.forEach((q, i) => (i ? ctx.lineTo(q.x, q.y) : ctx.moveTo(q.x, q.y))); ctx.closePath();
      stroke(3, 'rgba(0,0,0,.45)'); stroke(1.25, '#fff');
      ctx.beginPath(); ctx.moveTo(H.stem.x, H.stem.y); ctx.lineTo(H.rotate.x, H.rotate.y);
      stroke(3, 'rgba(0,0,0,.45)'); stroke(1.25, '#fff');
      const sz = 9 * k;
      H.corners.forEach((q) => {
        ctx.beginPath(); ctx.rect(q.x - sz / 2, q.y - sz / 2, sz, sz);
        ctx.fillStyle = '#fff'; ctx.fill(); stroke(1.25, 'rgba(0,0,0,.6)');
      });
      ctx.beginPath(); ctx.arc(H.rotate.x, H.rotate.y, 6 * k, 0, Math.PI * 2);
      ctx.fillStyle = '#fff'; ctx.fill(); stroke(1.25, 'rgba(0,0,0,.6)');
      ctx.restore();
    }
    function hitTest(pt) {
      const B = activeBox();
      if (B && B.view === view()) {
        const k = unit(), H = handlePoints(B), near = (q, r) => Math.hypot(pt.x - q.x, pt.y - q.y) <= r * k;
        if (near(H.rotate, 14)) return 'rotate';
        if (H.corners.some((q) => near(q, 13))) return 'scale';
        // inside the rotated box?
        const dx = pt.x - B.cx, dy = pt.y - B.cy, c = Math.cos(-B.rot), s2 = Math.sin(-B.rot);
        const lx = dx * c - dy * s2, ly = dx * s2 + dy * c;
        if (Math.abs(lx) <= B.w / 2 + 4 * k && Math.abs(ly) <= B.h / 2 + 4 * k) return 'move';
      }
      return null;
    }
    function areaAt(pt) {
      return p.areas.find((a) => {
        if (a.view !== view()) return false;
        const f = faceRect(a);
        return pt.x >= f.x && pt.x <= f.x + f.w && pt.y >= f.y && pt.y <= f.y + f.h;
      });
    }
    function draw() {
      const token = ++drawToken, v = variant();
      const paint = (img) => {
        if (token !== drawToken) return;
        sctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        paintScene(sctx, view(), dpr, S.step === 1, img);
      };
      if (v._img !== undefined) paint(v._img);
      else {
        paint(null);
        loadImg(v.image, v.fallbackImage).then((img) => { v._img = img; paint(img); });
        p.variants.forEach((vv) => loadImg(vv.image, vv.fallbackImage).then((img) => { vv._img = img; }));
      }
      root.querySelectorAll('[data-act="view"]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.view === view())));
    }
    async function imageFor(v) { if (v._img === undefined) v._img = await loadImg(v.image, v.fallbackImage); return v._img; }

    // Mouse, pen and touch: drag the artwork to move it, a corner to resize, the round handle to rotate.
    // Two fingers pinch to resize and twist to rotate. Tapping another area selects it.
    const toStage = (e) => {
      const r = stage.getBoundingClientRect();
      return { x: ((e.clientX - r.left) / r.width) * STAGE, y: ((e.clientY - r.top) / r.height) * SH };
    };
    const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
    const pointers = new Map();
    let drag = null, frame = 0;
    function redrawSoon() {
      if (frame) return;
      frame = requestAnimationFrame(() => { frame = 0; draw(); syncControls(); });
    }
    function startDrag(mode, pt) {
      dragging = true;
      cur()._autoLayout = false;
      const d = target(), B = activeBox();
      drag = { mode, start: pt, d0: { cx: d.cx, cy: d.cy, scale: d.scale, rot: d.rot }, B,
        dist0: Math.max(1, Math.hypot(pt.x - B.cx, pt.y - B.cy)), ang0: Math.atan2(pt.y - B.cy, pt.x - B.cx) };
    }
    function startPinch() {
      dragging = true;
      const [a, b] = [...pointers.values()], d = target();
      drag = { mode: 'pinch', d0: { cx: d.cx, cy: d.cy, scale: d.scale, rot: d.rot },
        dist0: Math.max(1, Math.hypot(b.x - a.x, b.y - a.y)), ang0: Math.atan2(b.y - a.y, b.x - a.x) };
    }
    const snapAngle = (deg, fine) => {
      deg = ((deg + 540) % 360) - 180;
      if (fine) return Math.round(deg / 15) * 15;
      for (const t of [-180, -90, 0, 90, 180]) if (Math.abs(deg - t) < 4) return t === -180 ? 180 : t;
      return Math.round(deg);
    };
    stage.addEventListener('pointerdown', (e) => {
      if (S.step !== 1) return;
      const pt = toStage(e);
      pointers.set(e.pointerId, pt);
      if (pointers.size === 2 && activeBox()) { startPinch(); e.preventDefault(); return; }
      if (pointers.size > 1) return;
      let mode = hitTest(pt);
      if (!mode) {
        const other = pieceBox(layerOf(cur()) === 'text' ? 'main' : 'text');
        if (other && other.view === view() && inBox(other, pt, 4 * unit())) {
          S.layer = other.which; render(); mode = 'move';
        }
      }
      if (mode) {
        stage.setPointerCapture(e.pointerId);
        startDrag(mode, pt);
        e.preventDefault();
        return;
      }
      const hit = areaAt(pt);
      if (hit && hit.key !== S.area) { S.area = hit.key; render(); return; }
      // Tap an empty spot in the current area to move the artwork there.
      if (hit && activeBox()) {
        const f = faceRect(hit), d = target();
        d.cx = clamp((pt.x - f.x) / f.w, 0, 1); d.cy = clamp((pt.y - f.y) / f.h, 0, 1);
        stage.setPointerCapture(e.pointerId);
        startDrag('move', pt);
        redrawSoon();
        e.preventDefault();
      }
    });
    stage.addEventListener('pointermove', (e) => {
      if (S.step !== 1) return;
      const pt = toStage(e);
      if (pointers.has(e.pointerId)) pointers.set(e.pointerId, pt);
      if (!drag) {
        const m = hitTest(pt);
        stage.style.cursor = m === 'move' ? 'move' : m === 'scale' ? 'nwse-resize' : m === 'rotate' ? 'grab'
          : areaAt(pt) && (multi || activeBox()) ? 'pointer' : 'default';
        return;
      }
      const d = target();
      if (drag.mode === 'pinch') {
        if (pointers.size < 2) return;
        const [a, b] = [...pointers.values()];
        d.scale = clamp(drag.d0.scale * Math.hypot(b.x - a.x, b.y - a.y) / drag.dist0, 0.1, 1.5);
        d.rot = snapAngle(drag.d0.rot + ((Math.atan2(b.y - a.y, b.x - a.x) - drag.ang0) * 180) / Math.PI, false);
      } else if (drag.mode === 'move') {
        const f = drag.B.f;
        d.cx = clamp(drag.d0.cx + (pt.x - drag.start.x) / f.w, 0, 1);
        d.cy = clamp(drag.d0.cy + (pt.y - drag.start.y) / f.h, 0, 1);
      } else if (drag.mode === 'scale') {
        d.scale = clamp(drag.d0.scale * Math.hypot(pt.x - drag.B.cx, pt.y - drag.B.cy) / drag.dist0, 0.1, 1.5);
      } else if (drag.mode === 'rotate') {
        const ang = Math.atan2(pt.y - drag.B.cy, pt.x - drag.B.cx);
        d.rot = snapAngle(drag.d0.rot + ((ang - drag.ang0) * 180) / Math.PI, e.shiftKey);
      }
      redrawSoon();
    });
    const endPointer = (e) => {
      pointers.delete(e.pointerId);
      if (drag && (drag.mode !== 'pinch' || pointers.size < 2)) { drag = null; dragging = false; draw(); renderLive(); syncControls(); }
    };
    stage.addEventListener('pointerup', endPointer);
    stage.addEventListener('pointercancel', endPointer);
    stage.addEventListener('lostpointercapture', (e) => { if (drag && drag.mode !== 'pinch') endPointer(e); });

    // Keyboard: arrows move (Shift for bigger steps), + and - resize, [ and ] rotate.
    stage.addEventListener('keydown', (e) => {
      if (S.step !== 1 || !activeBox()) return;
      cur()._autoLayout = false;
      const d = target(), step = e.shiftKey ? 0.05 : 0.01;
      const k = e.key;
      if (k === 'ArrowLeft') d.cx = clamp(d.cx - step, 0, 1);
      else if (k === 'ArrowRight') d.cx = clamp(d.cx + step, 0, 1);
      else if (k === 'ArrowUp') d.cy = clamp(d.cy - step, 0, 1);
      else if (k === 'ArrowDown') d.cy = clamp(d.cy + step, 0, 1);
      else if (k === '+' || k === '=') d.scale = clamp(d.scale * 1.05, 0.1, 1.5);
      else if (k === '-' || k === '_') d.scale = clamp(d.scale / 1.05, 0.1, 1.5);
      else if (k === ']') d.rot = snapAngle(d.rot + (e.shiftKey ? 15 : 1), false);
      else if (k === '[') d.rot = snapAngle(d.rot - (e.shiftKey ? 15 : 1), false);
      else return;
      e.preventDefault();
      redrawSoon();
      clearTimeout(stage._t); stage._t = setTimeout(renderLive, 250);
    });
    // Keep the sliders in step with on-canvas changes.
    function syncControls() {
      const d = target();
      const set = (b, v) => { const el = root.querySelector(`[data-bind="${b}"]`); if (el && document.activeElement !== el) el.value = v; };
      set('scalePct', Math.round(d.scale * 100));
      set('rot', Math.round(d.rot));
      const out = root.querySelector('.cc-rot-val'); if (out) out.textContent = `${Math.round(d.rot)}°`;
    }

    /* ---------- guidance ---------- */
    function issues(k) {
      const d = S.designs[k], out = [], a = areaOf(k);
      const add = (level, text) => out.push({ level, text });
      if (d.type === 'none') return out;
      if (d.type === 'logo') {
        if (!d.src) { add('info', multi ? 'Upload your artwork, or choose another option for this area.' : 'Upload your artwork, or choose Text.'); return out; }
        if (!d.src.vector && Math.min(d.src.naturalW, d.src.naturalH) < 600)
          add('warn', `Your file is ${d.src.naturalW} × ${d.src.naturalH} px, so edges may look soft. A file at least 1000 px wide, or an SVG, engraves sharper.`);
        if (d.mode === 'photo') add('info', 'Photos engrave as a fine dot pattern. On an area this small, a bold, high-contrast photo of one subject works best.');
        if (!d.rights) add('error', 'Confirm you own this artwork or have permission to use it.');
      }
      if (d.type === 'text' && !d.lines.some((l) => l.trim())) { add('error', 'Type at least one line' + (multi ? ', or choose Blank.' : '.')); return out; }
      if (d.type === 'mono' && !d.mono.last.trim()) { add('error', 'Add at least a last-name initial.'); return out; }
      if (hasTextLayer(d) && !textLines(d.textLayer)) add('info', 'Type your text, or choose Remove text.');
      const m = stageMask(k);
      if (m) {
        if (m.textFontPx) {
          const pt = (m.textFontPx / m.H) * areaIn(a).h * 72;
          if (pt < 9) add('warn', `Your text is about ${Math.round(pt)} pt tall. Make it larger or shorten it so it reads clearly.`);
        }
        if (m.fontPx) {
          const pt = (m.fontPx / m.H) * areaIn(a).h * 72;
          if (pt < 9) add('warn', `Your text is about ${Math.round(pt)} pt tall on this area. Fewer words per line will read more clearly.`);
        }
        if (d.type === 'logo' && d.mode !== 'photo' && !d.colorsOn.some(Boolean)) add('error', 'Turn on at least one color to engrave.');
        else if (d.type === 'logo' && m.coverage < 0.004) add('error', d.mode === 'photo' ? 'Nothing will engrave with these settings. Move the Brightness slider or turn on Swap light and dark.' : 'Nothing will engrave with these settings. Try less background removal.');
        else if (m.coverage > 0.6) add('warn', d.type === 'logo'
          ? 'Most of this design is solid engraving, which can look flat. Open Adjust artwork to leave a color clear, or try Photo.'
          : 'Most of this design is solid engraving, which can look flat.');
      }
      return out;
    }
    const hasErrors = (k) => issues(k).some((i) => i.level === 'error');

    function areaState(k) {
      const d = S.designs[k];
      if (d.type === 'logo' && !d.src) return 'Logo, no file yet';
      if (d.type === 'art') return artOf(d.artId) ? artOf(d.artId).label : 'Artwork';
      let s = TYPE_LABEL[d.type];
      if (d.type !== 'none' && hasErrors(k)) s += ', needs attention';
      return s;
    }
    function areaCharge(k) {
      // The first engraved area (in listed order) is included; each one after it is charged.
      if (!p.extraAreaPrice) return '';
      const list = engraved(), idx = list.indexOf(k);
      if (idx > 0) return `+${money(p.extraAreaPrice)}`;
      if (idx < 0 && list.length) return `Adds ${money(p.extraAreaPrice)}`;
      return list.length ? 'Included' : '';
    }
    // "Engraves as" thumbnail next to the customer's original file.
    function paintWhitePreview() {
      const c = root.querySelector('[data-white-preview]'), d = cur();
      if (!c || d.type !== 'logo' || !d.src) return;
      const src = d.src.canvas, box = 96 * dpr, s2 = Math.min(box / src.width, box / src.height);
      const w = Math.max(1, Math.round(src.width * s2)), h = Math.max(1, Math.round(src.height * s2));
      c.width = w; c.height = h;
      const x = c.getContext('2d');
      x.clearRect(0, 0, w, h);
      if (d.mode === 'photo') {
        const m = E.maskFromSource(src, w, h, { x: 0, y: 0, w, h }, { mode: 'photo', invert: d.invert, bright: d.bright });
        x.drawImage(E.paint(m, w, h, [250, 250, 248, 255]), 0, 0);
      } else {
        const f = faceRect(curArea()), Wst = Math.round(f.w * dpr), Hst = Math.round(f.h * dpr);
        E.fillOutlines(x, whiteLogo(d, curArea(), designBox(d, Wst, Hst), Wst), 0, 0, w, h);
      }
    }
    /* ---------- "Adjust your artwork" window ----------
       Opens after an upload (and from the Adjust artwork button) with a large, zoomable side-by-side of the
       customer's file and its white engraving, plus every conversion control. Done keeps the changes;
       Cancel or Esc restores exactly what was there before. */
    const adj = { open: false, key: null, snap: null, zoom: 1, bg: 'product', syncing: false };
    const adjustDlg = root.querySelector('.cc-adjust');
    const SNAP = ['mode', 'bgTol', 'gap', 'bright', 'invert', 'flip', 'lineShift'];
    const adjDesign = () => S.designs[adj.key];
    function openAdjust() {
      const d = cur();
      if (d.type !== 'logo' || !d.src || !adjustDlg) return;
      adj.open = true; adj.key = S.area;
      adj.snap = Object.fromEntries(SNAP.map((k) => [k, d[k]])); adj.snap.colorsOn = d.colorsOn.slice();
      const hex = variant().hex;
      adjustDlg.innerHTML = `
        <div class="cc-adjust-inner">
          <div class="cc-adjust-topbar">
            <button type="button" class="cc-adjust-top-btn" data-act="adjust-cancel">Cancel</button>
            <span class="cc-adjust-top-title" aria-hidden="true">Adjust</span>
            <button type="button" class="cc-adjust-top-btn cc-adjust-top-done" data-act="adjust-done">Done</button>
          </div>
          <div class="cc-adjust-head">
            <div><h2 id="cc-adjust-title" class="cc-h ${esc(cfg.headingClass)}">Adjust your artwork</h2>
              <p class="cc-hint">Everything that engraves turns white. Zoom in to check the details, then fine-tune below.</p></div>
            <button type="button" class="cc-adjust-x" data-act="adjust-cancel" aria-label="Close without changes">×</button>
          </div>
          <div class="cc-adjust-body">
            <div class="cc-adjust-views">
              <div class="cc-adjust-toolbar">
                <div class="cc-seg" role="group" aria-label="Zoom">
                  ${[[1, 'Fit'], [2, '2×'], [4, '4×']].map(([z, l]) => `<button type="button" data-act="adjust-zoom" data-z="${z}" aria-pressed="${adj.zoom === z}">${l}</button>`).join('')}
                </div>
                <div class="cc-seg" role="group" aria-label="Show engraving on">
                  <button type="button" data-act="adjust-bg" data-bg="product" aria-pressed="${adj.bg === 'product'}"><span class="cc-chip cc-chip-inline" style="--sw:${esc(hex)}"></span> ${esc(variant().color)}</button>
                  <button type="button" data-act="adjust-bg" data-bg="dark" aria-pressed="${adj.bg === 'dark'}">Dark</button>
                </div>
              </div>
              <div class="cc-adjust-pair">
                <figure><figcaption>Your file</figcaption><div class="cc-adjust-view cc-compare-orig" data-adjust-view><canvas data-adjust-orig></canvas></div></figure>
                <figure><figcaption>Engraves as</figcaption><div class="cc-adjust-view" data-adjust-view data-adjust-white></div></figure>
              </div>
            </div>
            <div class="cc-adjust-side">
              <p class="cc-msg cc-msg-info" data-adjust-note></p>
              <div class="cc-adjust-controls"></div>
            </div>
          </div>
          <div class="cc-nav">
            <button type="button" class="${btn2()}" data-act="adjust-cancel">Cancel</button>
            <button type="button" class="${btn()}" data-act="adjust-done">Done</button>
          </div>
        </div>`;
      // the two views scroll together, so zooming in compares the same spot
      const views = adjustDlg.querySelectorAll('[data-adjust-view]');
      views.forEach((v, i) => v.addEventListener('scroll', () => {
        if (adj.syncing) return;
        const o = views[1 - i], d2 = adjDesign();
        adj.syncing = true;
        o.scrollTop = v.scrollTop;
        // the engraving view is mirrored when the artwork is flipped
        o.scrollLeft = d2.flip ? (v.scrollWidth - v.clientWidth) - v.scrollLeft : v.scrollLeft;
        requestAnimationFrame(() => (adj.syncing = false));
      }));
      renderAdjustControls();
      if (!adjustDlg.open) adjustDlg.showModal();
      placeAdjust();
      applyZoom(1, adj.zoom);
      paintAdjust();
      const done = adjustDlg.querySelector('[data-act="adjust-done"]'); if (done) done.focus();
    }
    function closeAdjust(cancel) {
      if (!adj.open) return;
      const d = adjDesign();
      if (cancel && adj.snap) { SNAP.forEach((k) => (d[k] = adj.snap[k])); d.colorsOn = adj.snap.colorsOn.slice(); }
      adj.open = false;
      if (adjustDlg.open) adjustDlg.close();
      render();
      const b = root.querySelector('.cc-adjust-btn'); if (b) b.focus();
    }
    if (adjustDlg) adjustDlg.addEventListener('cancel', (e) => { e.preventDefault(); closeAdjust(true); }); // Esc
    function renderAdjustControls() {
      if (!adj.open) return;
      const box = adjustDlg.querySelector('.cc-adjust-controls'), d = adjDesign();
      // keep keyboard focus on the same control after re-rendering
      const f = document.activeElement && box.contains(document.activeElement) ? document.activeElement : null;
      const sel = f && (f.dataset.act ? `[data-act="${f.dataset.act}"]${f.dataset.i ? `[data-i="${f.dataset.i}"]` : ''}${f.dataset.mode ? `[data-mode="${f.dataset.mode}"]` : ''}` : f.dataset.bind ? `[data-bind="${f.dataset.bind}"]` : null);
      box.innerHTML = conversionControls(d);
      if (sel) { const g = box.querySelector(sel); if (g) g.focus(); }
    }
    function applyZoom(from, to) {
      adjustDlg.querySelectorAll('[data-adjust-view]').forEach((v) => {
        // keep the middle of the view in the middle when zooming
        const cx = (v.scrollLeft + v.clientWidth / 2) / Math.max(1, v.scrollWidth), cy = (v.scrollTop + v.clientHeight / 2) / Math.max(1, v.scrollHeight);
        if (v.firstElementChild) v.firstElementChild.style.width = `${to * 100}%`;
        v.scrollLeft = cx * v.scrollWidth - v.clientWidth / 2; v.scrollTop = cy * v.scrollHeight - v.clientHeight / 2;
      });
      adjustDlg.querySelectorAll('[data-act="adjust-zoom"]').forEach((b) => b.setAttribute('aria-pressed', String(+b.dataset.z === to)));
    }
    function paintAdjust() {
      if (!adj.open) return;
      const d = adjDesign(), a = areaOf(adj.key), src = d.src.canvas;
      const note = adjustDlg.querySelector('[data-adjust-note]'); if (note) note.textContent = conversionNote(d);
      const hex = adj.bg === 'dark' ? '#2b2f33' : variant().hex;
      // light finishes show the mark as light gray, like the product preview
      const ink = lum(hex) > 0.5 ? 'rgb(196,199,201)' : '#ffffff';
      const wv = adjustDlg.querySelector('[data-adjust-white]'), zoomW = `${adj.zoom * 100}%`;
      let W2, H2;
      if (d.mode === 'photo') {
        // photos engrave as a dot pattern, which is pixels by nature
        const k = Math.min(1, 1400 / Math.max(src.width, src.height));
        W2 = Math.round(src.width * k); H2 = Math.round(src.height * k);
        let c = wv.querySelector('canvas');
        if (!c) { wv.innerHTML = '<canvas></canvas>'; c = wv.querySelector('canvas'); }
        c.width = W2; c.height = H2; c.style.width = zoomW;
        const cx = c.getContext('2d'); cx.fillStyle = hex; cx.fillRect(0, 0, W2, H2);
        const m = E.maskFromSource(src, W2, H2, { x: 0, y: 0, w: W2, h: H2, flip: d.flip }, { mode: 'photo', invert: d.invert, bright: d.bright });
        cx.drawImage(E.paint(m, W2, H2, hexToRgb(ink === '#ffffff' ? '#ffffff' : '#c4c7c9').concat(255)), 0, 0);
      } else {
        // the converted logo as a real SVG: the same vector outlines that make the laser file
        const f = faceRect(a), Wst = Math.round(f.w * dpr), Hst = Math.round(f.h * dpr);
        const V = whiteLogo(d, a, designBox(d, Wst, Hst), Wst);
        W2 = V.W; H2 = V.H;
        wv.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W2} ${H2}" style="width:${zoomW};display:block" role="img" aria-label="Your artwork as it will engrave">
          <rect width="${W2}" height="${H2}" fill="${hex}"/>
          <path fill="${ink}" fill-rule="evenodd" ${d.flip ? `transform="translate(${W2} 0) scale(-1 1)"` : ''} d="${E.outlinesToSvgPath(V)}"/></svg>`;
      }
      adjustDlg.querySelectorAll('[data-adjust-view]').forEach((v) => {
        v.style.aspectRatio = `${W2} / ${H2}`;
        v.style.height = `${Math.round((v.clientWidth * H2) / W2)}px`; // explicit height: some Safari versions ignore aspect-ratio here
      });
      // the customer's own file, as uploaded (trimmed), for comparison
      const oc = adjustDlg.querySelector('[data-adjust-orig]');
      if (oc.width !== W2 || oc.height !== H2) { oc.width = W2; oc.height = H2; }
      const ox = oc.getContext('2d'); ox.clearRect(0, 0, W2, H2); ox.imageSmoothingQuality = 'high'; ox.drawImage(src, 0, 0, W2, H2);
    }

    function renderLive() {
      paintWhitePreview();
      paintAdjust();
      const box = $('.cc-issues');
      if (box) {
        const d = cur(), notes = [];
        if (d.error) notes.push({ level: 'error', text: d.error });
        if (d.type === 'logo' && d.an) notes.push({ level: 'info', text: conversionNote(d) });
        else if (d.note) notes.push({ level: 'info', text: d.note });
        box.innerHTML = notes.concat(issues(S.area)).map((i) => `<p class="cc-msg cc-msg-${i.level}">${esc(i.text)}</p>`).join('');
      }
      renderAreaStates();
      renderPrice();
    }
    function renderPrice() {
      const el = $('.cc-price');
      if (!priced) {
        el.textContent = p.extraAreaPrice && multi ? `Each additional engraving area adds ${money(p.extraAreaPrice)}.` : '';
        return;
      }
      el.innerHTML = `${money(unitPrice())}${extraCount() ? ` <span>includes ${extraCount()} additional area${extraCount() > 1 ? 's' : ''}</span>` : ''}`;
    }

    /* ---------- views ---------- */
    function renderChrome() {
      const names = ['Design', 'Review'];
      $('.cc-steps').innerHTML = names.map((n, i) => {
        const s = i + 1, state = s === S.step ? 'current' : s < S.step ? 'done' : '';
        return `<li class="${state}"><button type="button" data-act="goto" data-step="${s}" ${s > S.maxStep ? 'disabled' : ''} ${s === S.step ? 'aria-current="step"' : ''}><span class="cc-num">${s}</span>${n}</button></li>`;
      }).join('');
      $('.cc-view').innerHTML = p.views.length > 1
        ? p.views.map((vw) => `<button type="button" data-act="view" data-view="${vw.key}">${esc(vw.label)}</button>`).join('') : '';
      $('.cc-stage-note').textContent = S.step !== 1
        ? 'Preview is a close simulation. Laser engraving on anodized aluminum leaves a bright white, permanent mark.'
        : multi ? 'Select an outlined area to design it, then drag, resize or rotate your artwork right here.'
          : 'Drag, resize or rotate your artwork right here. Laser engraving leaves a bright white, permanent mark.';
      stage.setAttribute('aria-label', S.step === 1
        ? `Preview of your engraved ${p.title}. When your artwork is selected, use the arrow keys to move it, plus and minus to resize, and the bracket keys to rotate.`
        : `Proof of your engraved ${p.title}`);
    }

    const slider = (label, bind, min, max, value, left, right) => `
      <label class="cc-field"><span class="cc-label">${label}</span>
        <input type="range" min="${min}" max="${max}" value="${value}" data-bind="${bind}">
        <span class="cc-range-ends"><span>${left}</span><span>${right}</span></span></label>`;
    const seg = (act, items, current, attr) => `<div class="cc-seg" role="group">${items.map((it) =>
      `<button type="button" data-act="${act}" data-${attr}="${it.id}" aria-pressed="${it.id === current}" ${it.style ? `style="${it.style}"` : ''}>${esc(it.label)}</button>`).join('')}</div>`;
    function textLayerHtml(d) {
      if (!textCapable(d)) return '';
      const what = d.type === 'art' ? 'artwork' : 'logo';
      if (!d.textLayer) return `<button type="button" class="${btn2('cc-add-text')}" data-act="text-add">+ Add text</button>
        <p class="cc-hint cc-hint-tight">Add a name, a date or a slogan with your ${what}.</p>`;
      const t = d.textLayer;
      return `<div class="cc-textlayer">
        <p class="cc-label">Text with your ${what}</p>
        ${[0, 1].map((i) => `<label class="cc-field"><span class="cc-label">Line ${i + 1}${i ? ' (optional)' : ''}</span>
          <input type="text" maxlength="24" value="${esc(t.lines[i])}" data-bind="tline${i}" autocomplete="off"></label>`).join('')}
        <div class="cc-field"><span class="cc-label">Lettering</span>
          ${seg('tfont', E.FONTS.map((f) => ({ id: f.id, label: f.label, style: `font-family:'${f.id}';font-weight:${f.weight}` })), t.font, 'font')}</div>
        <button type="button" class="cc-link" data-act="text-remove">Remove text</button>
      </div>`;
    }
    // As lines are added the text block grows taller; keep it inside the engraving area by moving it up,
    // and if it still doesn't fit, by making it a little smaller.
    function keepTextInside(d) {
      const t = d.textLayer, f = faceRect(curArea()), margin = 0.04;
      for (let i = 0; i < 12; i++) {
        const bx = designBox(textObj(t), f.w, f.h), top = bx.y / f.h, bottom = (bx.y + bx.h) / f.h;
        if (bottom <= 1 - margin && top >= margin) return;
        if (bottom > 1 - margin) t.cy -= bottom - (1 - margin);
        const top2 = designBox(textObj(t), f.w, f.h).y / f.h;
        if (top2 < margin) { t.cy += margin - top2; t.scale *= 0.92; }
      }
    }
    // Until the customer positions things themselves, stack the logo (or fish) above the text: the text keeps
    // the space it needs at the bottom and the logo is fitted, centered, into the space above it.
    function autoLayout(d) {
      if (!d._autoLayout || !hasTextLayer(d)) return;
      const f = faceRect(curArea()), margin = 0.06, gap = 0.04, t = d.textLayer;
      // the text takes at most about a third of the height (two lines a little more), along the bottom
      const maxH = textLines(t) && t.lines.filter((l) => l.trim()).length > 1 ? 0.4 : 0.3;
      t.cx = 0.5; t.rot = 0; t.scale = Math.max(t.scale, 0.3);
      for (let i = 0; i < 30 && designBox(textObj(t), f.w, f.h).h / f.h > maxH; i++) t.scale *= 0.95;
      const th = designBox(textObj(t), f.w, f.h).h / f.h;
      t.cy = 1 - margin - th / 2;
      keepTextInside(d);
      const tb = designBox(textObj(t), f.w, f.h), textTop = tb.y / f.h;
      const room = textTop - gap - margin;
      if (room <= 0.1) return;
      d.cx = 0.5; d.rot = 0; d.scale = 0.9;
      for (let i = 0; i < 40; i++) {
        const mb = designBox(d, f.w, f.h);
        if (mb.h / f.h <= room + 1e-3 && mb.w / f.w <= 0.9 + 1e-3) break;
        d.scale *= 0.96;
      }
      d.cy = margin + room / 2;
    }
    const placement = (d0) => {
      const d = cur(), t = target();
      const sw = hasTextLayer(d) ? `<div class="cc-field"><span class="cc-label">Adjusting</span>
        ${seg('layer', [{ id: 'main', label: d.type === 'art' ? 'Artwork' : 'Logo' }, { id: 'text', label: 'Text' }], layerOf(d), 'layer')}</div>` : '';
      return textLayerHtml(d) + sw + placementSliders(t);
    };
    const placementSliders = (d) => `
      <div class="cc-place">
        <p class="cc-place-hint">Drag your artwork on the preview to move it. Drag a corner to resize it, or the round handle to rotate it.</p>
        ${slider('Size', 'scalePct', 10, 150, Math.round(d.scale * 100), 'Smaller', 'Larger')}
        <label class="cc-field"><span class="cc-label">Rotation <span class="cc-rot-val cc-label-value">${Math.round(d.rot)}°</span></span>
          <input type="range" min="-180" max="180" value="${Math.round(d.rot)}" data-bind="rot">
          <span class="cc-range-ends"><span>-180°</span><span>180°</span></span></label>
        <div class="cc-place-actions">
          <button type="button" class="cc-link" data-act="center">Center it</button>
          <button type="button" class="cc-link" data-act="straighten">Straighten</button>
        </div>
      </div>`;

    function colorPicker() {
      const v = variant();
      return `
        <fieldset class="cc-field cc-color">
          <legend class="cc-label">Color: <span class="cc-label-value">${esc(v.color)}</span></legend>
          <div class="cc-swatches">
            ${p.variants.map((vv, i) => `<button type="button" class="cc-swatch" data-act="color" data-i="${i}" aria-pressed="${i === S.v}"
              aria-label="${esc(vv.color)}${vv.available === false ? ' (sold out)' : ''}" title="${esc(vv.color)}" ${vv.available === false ? 'disabled' : ''}
              style="--sw:${esc(vv.hex)}"></button>`).join('')}
          </div>
          ${lum(v.hex) > 0.5 ? '<p class="cc-msg cc-msg-info">On light colors, the engraving shows as a subtle light-gray mark rather than bright white.</p>' : ''}
        </fieldset>`;
    }

    // Controls that change how an uploaded file converts to white engraving (shown in the Adjust window).
    function conversionControls(d) {
      return `
        <div class="cc-field"><span class="cc-label">This file is a</span>
          ${seg('mode', [{ id: 'logo', label: 'Logo or graphic' }, { id: 'photo', label: 'Photo' }], d.mode, 'mode')}</div>
        ${d.mode === 'photo' ? `
          ${slider('Brightness', 'bright', -60, 60, d.bright, 'Darker', 'Brighter')}
          <label class="cc-check"><input type="checkbox" data-bind="invert" ${d.invert ? 'checked' : ''}>
            <span>Swap light and dark</span></label>` : `
          ${d.an.palette.length > 1 ? `
            <div class="cc-field"><span class="cc-label">Colors to engrave</span>
              <p class="cc-hint cc-hint-tight">Everything engraves white. Turn a color off to leave it clear, for example to let lettering show through a solid badge.</p>
              <div class="cc-colors" role="group" aria-label="Colors to engrave">
                ${d.an.palette.map((c, i) => `<button type="button" data-act="logo-color" data-i="${i}" aria-pressed="${d.colorsOn[i] !== false}"
                  aria-label="Color ${i + 1} ${d.colorsOn[i] !== false ? 'engraves' : 'stays clear'}"><span class="cc-chip" style="--sw:${c.hex}"></span>${d.colorsOn[i] !== false ? 'Engrave' : 'Leave clear'}</button>`).join('')}
              </div></div>
            ${d.colorsOn.filter(Boolean).length > 1 ? `<label class="cc-check"><input type="checkbox" data-bind="gap" ${d.gap ? 'checked' : ''}>
              <span>Keep a thin gap between colors<small>Keeps touching colors from merging into one white shape.</small></span></label>` : ''}` : ''}
          ${d.an.bgType === 'color' ? slider(`Background removal <span class="cc-chip cc-chip-inline" style="--sw:${d.an.bgHex}"></span>`, 'bgTol', 10, 70, d.bgTol, 'Less', 'More') : ''}
          ${d.an.lineCut != null ? slider('Line detail', 'lineShift', -20, 20, d.lineShift || 0, 'Outlines only', 'More shading') : ''}`}
        <label class="cc-check"><input type="checkbox" data-bind="flip" ${d.flip ? 'checked' : ''}><span>Flip left to right</span></label>`;
    }

    function areaPicker(pricing) {
      return `
        <div class="cc-field">
          <span class="cc-label">Engraving areas</span>
          <p class="cc-hint cc-hint-tight">Engrave one area or several.${pricing || ''}</p>
          <div class="cc-areas" role="tablist" aria-label="Engraving area">
            ${p.areas.map((ar) => `<button type="button" role="tab" data-act="area" data-area="${ar.key}" aria-selected="${ar.key === S.area}">
              <span class="cc-area-name">${esc(ar.label)}</span>
              <span class="cc-area-state"></span>
              ${p.extraAreaPrice ? '<span class="cc-area-price"></span>' : ''}</button>`).join('')}
          </div>
        </div>`;
    }
    const typeItems = (a) => [
      ...TYPES,
      ...(galleryFor(a) ? [{ id: 'art:' + ((cur().type === 'art' && artOf(cur().artId)) ? cur().artId : artReady(a.art)[0].id), label: 'Artwork' }]
        : offersArt(a) ? artReady(a.art).map((it) => ({ id: 'art:' + it.id, label: it.label })) : []),
      ...(multi ? [BLANK] : []),
    ];
    function typePicker() {
      const d = cur(), a = curArea();
      const onFile = DEALER || d.type === 'onfile' ? `<label class="cc-check cc-onfile"><input type="checkbox" data-bind="onfile" ${d.type === 'onfile' ? 'checked' : ''}>
        <span>Use artwork on file</span></label>` : '';
      if (d.type === 'onfile') return onFile;
      return `${onFile}<div class="cc-field"><span class="cc-label">What should we engrave${multi ? ' here' : ''}?</span>
        ${seg('type', typeItems(a), d.type === 'art' ? 'art:' + d.artId : d.type, 'type')}</div>`;
    }

    /* ---------- phones: toolbar under the preview + pop-up menus ----------
       On narrow screens the preview stays pinned at the top. Color, engraving area and design type move into
       a toolbar right under it; each button opens a menu from the bottom of the screen with the same choices
       as on desktop. Picking one updates the preview and closes the menu. */
    // Phones: how much of the pinned block is toolbar and spacing, so the preview image can be sized to keep
    // the whole block within half the screen (cling-customizer.css uses --cc-pin-chrome).
    function measurePinChrome() {
      const wrap = root.querySelector('.cc-stage-wrap'), cv = root.querySelector('.cc-stage');
      if (!wrap || !cv || !global.matchMedia || !matchMedia('(max-width: 760px)').matches) return;
      const chrome = Math.max(0, Math.round(wrap.getBoundingClientRect().height - cv.getBoundingClientRect().height));
      if (chrome && Math.abs(chrome - (measurePinChrome.last || 0)) > 1) {
        measurePinChrome.last = chrome;
        root.style.setProperty('--cc-pin-chrome', chrome + 'px');
      }
    }
    global.addEventListener('resize', () => measurePinChrome());
    function renderAreaStates() {
      root.querySelectorAll('[data-act="area"]').forEach((b) => {
        const k = b.dataset.area;
        b.querySelector('.cc-area-state').textContent = areaState(k);
        const c = b.querySelector('.cc-area-price'); if (c) c.textContent = areaCharge(k);
      });
    }
    function renderMTools() {
      const box = root.querySelector('[data-mtools]'); if (!box) return;
      if (S.step !== 1) { box.innerHTML = ''; return; }
      const d = cur(), a = curArea(), v = variant();
      const typeLabel = d.type === 'art' ? (artOf(d.artId) ? artOf(d.artId).label : 'Artwork') : TYPE_LABEL[d.type];
      box.innerHTML = `
        <button type="button" data-act="sheet" data-sheet="color" aria-haspopup="dialog">
          <span class="cc-mtool-k">Color</span><span class="cc-mtool-v"><span class="cc-chip" style="--sw:${esc(v.hex)}"></span>${esc(v.color)}</span></button>
        ${multi ? `<button type="button" data-act="sheet" data-sheet="area" aria-haspopup="dialog">
          <span class="cc-mtool-k">Area</span><span class="cc-mtool-v">${esc(a.label.replace(/\s+panel$/i, ''))}</span></button>` : ''}
        <button type="button" data-act="sheet" data-sheet="design" aria-haspopup="dialog">
          <span class="cc-mtool-k">Design</span><span class="cc-mtool-v">${esc(typeLabel)}</span></button>`;
    }
    const SHEETS = {
      color: { title: 'Color', body: () => colorPicker() },
      area: { title: 'Engraving area', body: () => areaPicker(multi && p.extraAreaPrice ? ` Your first area is included, and each additional area adds ${money(p.extraAreaPrice)}.` : '') },
      design: { title: 'Design', body: () => typePicker() },
    };
    /* Phones: Safari reports its bars inconsistently to CSS (when the bottom bar collapses it can still cover
       the screen's bottom edge while CSS says nothing is covered). visualViewport tracks the area that's really
       visible, so the menus and the Adjust window are placed from it. */
    const phone = () => !!(global.matchMedia && matchMedia('(max-width: 760px)').matches);
    const visibleBox = () => {
      const vv = global.visualViewport;
      return vv ? { top: vv.offsetTop, height: vv.height } : { top: 0, height: global.innerHeight };
    };
    // Menus drop down from the toolbar, so they never reach the bottom of the screen.
    function placeSheet() {
      const sh = root.querySelector('.cc-sheet');
      if (!sh || sh.hidden || !phone()) return;
      const tb = root.querySelector('[data-mtools]'), v = visibleBox(), bottom = v.top + v.height - 12;
      let top = Math.max(v.top + 8, tb ? tb.getBoundingClientRect().bottom + 8 : v.top + 8);
      // too tall for the space under the toolbar (a small phone): slide it up just enough, over the preview
      sh.style.maxHeight = 'none';
      const need = sh.scrollHeight;
      if (top + need > bottom) top = Math.max(v.top + 8, bottom - need);
      sh.style.top = Math.round(top) + 'px';
      sh.style.maxHeight = Math.max(160, Math.round(bottom - top)) + 'px';
    }
    // The Adjust window fills exactly the visible area, so Cancel and Done are always on screen.
    function placeAdjust() {
      if (!adjustDlg || !adjustDlg.open) return;
      if (!phone()) { adjustDlg.style.top = adjustDlg.style.height = adjustDlg.style.maxHeight = ''; return; }
      const v = visibleBox();
      adjustDlg.style.top = Math.round(v.top) + 'px';
      adjustDlg.style.height = adjustDlg.style.maxHeight = Math.round(v.height) + 'px';
    }
    const placeOverlays = () => { placeSheet(); placeAdjust(); };
    if (global.visualViewport) { visualViewport.addEventListener('resize', placeOverlays); visualViewport.addEventListener('scroll', placeOverlays); }
    global.addEventListener('resize', placeOverlays);
    global.addEventListener('scroll', placeOverlays, { passive: true });

    function renderSheet() {
      const sh = root.querySelector('.cc-sheet'), bd = root.querySelector('.cc-sheet-backdrop');
      if (!sh) return;
      const def = S.sheet && SHEETS[S.sheet];
      sh.hidden = bd.hidden = !def;
      if (!def) return;
      sh.innerHTML = `
        <div class="cc-sheet-head"><p class="cc-sheet-title" id="cc-sheet-title">${esc(def.title)}</p>
          <button type="button" class="cc-sheet-close" data-act="sheet-close" aria-label="Close">×</button></div>
        <div class="cc-sheet-body">${def.body()}</div>`;
      renderAreaStates();
      placeSheet();
    }
    function openSheet(name) {
      S.sheet = name; renderSheet();
      const first = root.querySelector('.cc-sheet [aria-pressed="true"], .cc-sheet [aria-selected="true"], .cc-sheet button:not(.cc-sheet-close)');
      if (first) first.focus();
    }
    function closeSheet() {
      const was = S.sheet; S.sheet = null; renderSheet();
      const b = root.querySelector(`[data-sheet="${was}"]`); if (b) b.focus();
    }

    function viewDesign() {
      const d = cur(), a = curArea(), size = areaIn(a);
      let controls = '';
      if (d.type === 'logo') {
        controls = !d.src ? `
          <label class="cc-drop" data-drop>
            <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml,.svg,application/pdf,.pdf,.ai,.eps,application/postscript,application/illustrator" data-file hidden>
            <span class="cc-drop-title">${S.busy ? 'Reading your file…' : 'Choose a file or drop it here'}</span>
            <span class="cc-drop-sub">PNG, JPG, WEBP, SVG, PDF or Adobe Illustrator (AI, EPS) up to 20 MB. Vector files (SVG, PDF, AI) engrave sharpest; a one-color logo on a plain or transparent background engraves cleanest.</span>
          </label>` : `
          ${d.libraryId ? `<p class="cc-hint cc-lib-from">From your saved artwork: <strong>${esc(d.libraryName || d.fileName)}</strong>.
            <button type="button" class="cc-link" data-act="lib-back">Choose other saved artwork</button></p>` : ''}
          <div class="cc-file"><span class="cc-file-name">${esc(d.fileName)}</span>
            <label class="cc-link">Replace<input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml,.svg,application/pdf,.pdf,.ai,.eps,application/postscript,application/illustrator" data-file hidden></label>
            <button type="button" class="cc-link" data-act="remove-file">Remove</button></div>
          <button type="button" class="cc-compare" data-act="adjust-open" aria-label="Adjust how your artwork converts to engraving">
            <figure><span class="cc-compare-img cc-compare-orig"><img src="${d.thumb}" alt=""></span><figcaption>Your file</figcaption></figure>
            <span class="cc-compare-arrow" aria-hidden="true">→</span>
            <figure><span class="cc-compare-img cc-compare-white"><canvas data-white-preview></canvas></span><figcaption>Engraves as</figcaption></figure>
          </button>
          <button type="button" class="${btn2('cc-adjust-btn')}" data-act="adjust-open">Adjust artwork</button>
          ${placement(d)}
          <label class="cc-check"><input type="checkbox" data-bind="rights" ${d.rights ? 'checked' : ''}>
            <span>I own this artwork or have permission to use it.</span></label>`;
      } else if (d.type === 'art') {
        const it = artOf(d.artId);
        const gallery = galleryFor(a) ? `
          <div class="cc-field"><span class="cc-label">Choose artwork</span>
            <div class="cc-art cc-art-wide" role="group" aria-label="Artwork">
              ${artReady(a.art).map((x) => `<button type="button" data-act="art" data-id="${esc(x.id)}" aria-pressed="${x.id === d.artId}">
                <span class="cc-art-thumb"><img src="${esc(x.url)}" alt=""></span><span>${esc(x.label)}</span></button>`).join('')}
            </div></div>` : '';
        controls = !it ? gallery : isFitted(d) ? `${gallery}
          <p class="cc-hint">${esc(it.label)} is made for the ${esc(a.label.toLowerCase())}. It's centered and sized to fill it edge to edge.</p>` : `${gallery}
          <p class="cc-hint">The ${esc(it.label)} faces ${artFacing(d)}${d.flip === defaultFlip(d.artId, a) ? `, toward the center of your ${esc(p.title)}` : ''}.</p>
          <label class="cc-check"><input type="checkbox" data-bind="flip" ${d.flip !== defaultFlip(d.artId, a) ? 'checked' : ''} data-artflip>
            <span>Face the other way</span></label>
          ${placement(d)}`;
      } else if (d.type === 'text') {
        controls = `
          ${[0, 1, 2].map((i) => `<label class="cc-field"><span class="cc-label">Line ${i + 1}${i ? ' (optional)' : ''}</span>
            <input type="text" maxlength="24" value="${esc(d.lines[i])}" data-bind="line${i}" autocomplete="off"></label>`).join('')}
          <div class="cc-field"><span class="cc-label">Lettering</span>
            ${seg('font', E.FONTS.map((f) => ({ id: f.id, label: f.label, style: `font-family:'${f.id}';font-weight:${f.weight}` })), d.font, 'font')}</div>
          ${placement(d)}`;
      } else if (d.type === 'mono') {
        controls = `
          <div class="cc-mono-inputs">
            ${[['first', 'First'], ['middle', 'Middle'], ['last', 'Last']].map(([k, l]) => `<label class="cc-field"><span class="cc-label">${l}</span>
              <input type="text" maxlength="1" value="${esc(d.mono[k])}" data-bind="mono.${k}" autocomplete="off" class="cc-initial"></label>`).join('')}
          </div>
          <p class="cc-hint">A classic monogram puts the last-name initial large in the middle.</p>
          <div class="cc-field"><span class="cc-label">Style</span>
            ${seg('monostyle', [{ id: 'classic', label: 'Classic serif' }, { id: 'block', label: 'Block' }], d.monoStyle, 'style')}</div>
          ${placement(d)}`;
      } else {
        controls = `<p class="cc-hint cc-blank">The ${esc(a.label.toLowerCase())} stays blank.</p>`;
      }
      // artwork on file (dealers): their company's saved artwork to pick from, or a note saying which artwork to use
      if (d.type === 'onfile') {
        const L = S.library, items = L && L.items ? L.items : [];
        if (!L) setTimeout(loadLibrary, 0);
        controls = `
          ${!L || L === 'loading' ? '<p class="cc-hint">Loading your saved artwork…</p>' : items.length ? `
          <p class="cc-label">Your saved artwork</p>
          <div class="cc-lib">${items.map((it) => `
            <button type="button" class="cc-lib-item" data-act="lib-pick" data-id="${esc(it.id)}" ${S.busy ? 'disabled' : ''}>
              <span class="cc-lib-img">${it.thumb ? `<img src="${esc(libUrl(it.thumb))}" alt="" loading="lazy">` : `<span>${esc((it.fileName.split('.').pop() || 'FILE').toUpperCase())}</span>`}</span>
              <span class="cc-lib-name">${esc(it.name)}</span>
            </button>`).join('')}</div>
          <p class="cc-hint">Pick one to place and size it yourself, with a proof.</p>
          <p class="cc-label">Not in your saved artwork?</p>` : `
          <p class="cc-hint">Logos you upload on dealer orders are saved here, so next time you can just pick them.</p>`}
          <p class="cc-hint">We'll engrave the artwork we have on file for your company${multi ? ' in this area' : ''}. The words on the preview are a placeholder; our team places and sizes your artwork.</p>
          <label class="cc-field"><span class="cc-label">Which artwork? (optional)</span>
            <input type="text" maxlength="200" data-bind="onFileNote" value="${esc(d.onFileNote || '')}" placeholder="Example: same as order #1043, or our round logo"></label>`;
      }
      const pricing = multi && p.extraAreaPrice
        ? ` Your first area is included, and each additional area adds ${money(p.extraAreaPrice)}.` : '';
      return `
        <div class="cc-m-sheetable">${colorPicker()}</div>
        ${multi ? `<div class="cc-m-sheetable">${areaPicker(pricing)}</div>` : ''}
        <div class="cc-editor">
          <h2 class="cc-h ${esc(cfg.headingClass)}">${multi ? esc(a.label) : 'Your design'}</h2>
          <p class="cc-hint">About ${fmtIn(size.w)} × ${fmtIn(size.h)} in. The dashed outline on the preview shows the engraving area.</p>
          <div class="cc-m-sheetable">${typePicker()}</div>
          <div class="cc-controls">${controls}</div>
          <div class="cc-issues"></div>
        </div>
        <details class="cc-tips"><summary>Tips for a clean engraving</summary>
          <ul>
            <li>Everything engraves in one tone. Colors become solid white marks, and shading becomes a dot pattern.</li>
            <li>Engraving areas are small, so bold, simple shapes read best. Lines thinner than about 0.3 mm may not show up.</li>
            <li>Transparent PNG or SVG files give the cleanest edges. Screenshots and photos of printed logos usually look rough.</li>
            <li>Have a PDF or Adobe Illustrator file? Export it as a PNG or SVG first.</li>
          </ul>
        </details>`;
    }

    function describe(k) {
      const d = S.designs[k];
      if (!isEngraved(k)) return 'Blank';
      if (d.type === 'art') return isFitted(d) ? artOf(d.artId).label : `${artOf(d.artId).label}, facing ${artFacing(d)}`;
      if (d.type === 'logo') return `Logo (${d.fileName})${d.flip ? ', flipped' : ''}`;
      if (d.type === 'text') return `Text: “${d.lines.filter((l) => l.trim()).join(' / ')}”`;
      if (d.type === 'mono') return `Monogram: ${[d.mono.first, d.mono.last, d.mono.middle].filter(Boolean).join('').toUpperCase()}`;
      if (d.type === 'onfile') return 'Artwork on file' + (d.onFileNote && d.onFileNote.trim() ? ` (${d.onFileNote.trim()})` : '');
      return 'Blank';
    }
    // Same as describe(), as HTML, with anything the customer typed or named marked to keep its case.
    function describeHtml(k) {
      const d = S.designs[k], v = (t) => `<span class="cc-verbatim">${esc(t)}</span>`;
      const withText = hasTextLayer(d) && textLines(d.textLayer) ? ` + text “${v(d.textLayer.lines.filter((l) => l.trim()).join(' / '))}”` : '';
      if (isEngraved(k) && d.type === 'logo') return `Logo (${v(d.fileName)})${d.flip ? ', flipped' : ''}${withText}`;
      if (isEngraved(k) && d.type === 'art' && withText) return esc(describe(k)) + withText;
      if (isEngraved(k) && d.type === 'text') return `Text: “${v(d.lines.filter((l) => l.trim()).join(' / '))}”`;
      return esc(describe(k));
    }
    function viewReview() {
      const v = variant(), viewsUsed = p.views.filter((vw) => p.areas.some((a) => a.view === vw.key && engraved().includes(a.key)));
      const warns = engraved().flatMap((k) => issues(k).filter((i) => i.level === 'warn').map((i) => (multi ? areaOf(k).label + ': ' : '') + i.text));
      return `
        <h2 class="cc-h ${esc(cfg.headingClass)}">Check your proof</h2>
        <p class="cc-hint">This is what we'll engrave. Check spelling, size and placement closely, because we engrave exactly what you approve.</p>
        <div class="cc-proofs">
          ${chosen().flatMap((c) => viewsUsed.map((vw) => `<figure><canvas data-proof="${vw.key}" data-vi="${c.i}" width="${Math.round(STAGE * dpr * 0.5)}" height="${Math.round(SH * dpr * 0.5)}"></canvas>
            <figcaption>${esc(c.v.color)}${viewsUsed.length > 1 ? `, ${esc(vw.label)}` : ''}</figcaption></figure>`)).join('')}
        </div>
        <button type="button" class="${btn2('cc-download-proof')}" data-act="download-proof">Download proof</button>
        <div class="cc-cq" data-cq-box>${colorQtyHtml()}</div>
        <dl class="cc-summary">
          <div><dt>Colors</dt><dd data-cq-summary>${esc(chosen().map((c) => `${c.v.color} × ${c.n}`).join(', '))}</dd></div>
          ${multi
            ? p.areas.map((a) => `<div><dt>${esc(a.label)}</dt><dd>${describeHtml(a.key)}${p.extraAreaPrice && engraved().indexOf(a.key) > 0 ? ` <span class="cc-muted">+${money(p.extraAreaPrice)}</span>` : ''}</dd></div>`).join('')
            : `<div><dt>Engraving</dt><dd>${describeHtml(p.areas[0].key)}</dd></div>`}
          <div><dt>Units</dt><dd data-cq-units>${totalQty()}</dd></div>
          ${priced ? `<div><dt>Total</dt><dd class="cc-total">${money(total())}</dd></div>`
            : extraCount() && p.extraAreaPrice ? `<div><dt>Extra areas</dt><dd class="cc-total">+${money(p.extraAreaPrice * extraCount() * totalQty())}</dd></div>` : ''}
        </dl>
        ${TIERS.length ? `<div class="cc-bulk" data-bulk aria-live="polite">${bulkHtml()}</div>` : ''}
        ${DEALER ? `<div class="cc-bulk cc-dealer" data-dealer aria-live="polite">${dealerHtml()}</div>` : ''}
        ${warns.map((w) => `<p class="cc-msg cc-msg-warn">${esc(w)}</p>`).join('')}
        <label class="cc-field"><span class="cc-label">Notes for our engraving team (optional)</span>
          <textarea rows="3" maxlength="500" data-bind="notes" placeholder="Example: keep the logo a little higher">${esc(S.notes)}</textarea></label>
        <label class="cc-check cc-approve${S.approved ? ' is-approved' : ''}"><input type="checkbox" data-bind="approved" ${S.approved ? 'checked' : ''}>
          <span>I've checked spelling, size and placement, and I approve this proof. I understand custom engraved items can't be returned.</span></label>`;
    }

    /* ---------- colors and quantities (review step) ---------- */
    function colorQtyHtml() {
      const m = qtyMap();
      return `
        <p class="cc-label">Colors and quantities</p>
        <p class="cc-hint">Order this design in as many colors as you like. ${DEALER ? `Every unit counts toward your dealer minimum of ${DEALER_MIN}.` : 'Every unit counts toward your bulk discount.'}</p>
        <div class="cc-cq-list">
          ${p.variants.map((vv, i) => {
            const n = m[i] || 0, off = vv.available === false;
            return `<div class="cc-cq-row${n ? ' is-on' : ''}${off ? ' is-off' : ''}">
              <span class="cc-chip" style="--sw:${esc(vv.hex)}"></span>
              <span class="cc-cq-name">${esc(vv.color)}${off ? ' <small>Sold out</small>' : ''}</span>
              ${priced ? `<span class="cc-cq-price">${money(vv.price)}</span>` : '<span></span>'}
              <span class="cc-stepper">
                <button type="button" data-act="cq-step" data-i="${i}" data-d="-1" aria-label="One fewer ${esc(vv.color)}" ${off || !n ? 'disabled' : ''}>−</button>
                <input type="number" min="0" max="500" value="${n}" data-cq="${i}" inputmode="numeric" aria-label="${esc(vv.color)} quantity" ${off ? 'disabled' : ''}>
                <button type="button" data-act="cq-step" data-i="${i}" data-d="1" aria-label="One more ${esc(vv.color)}" ${off ? 'disabled' : ''}>+</button>
              </span>
            </div>`;
          }).join('')}
        </div>`;
    }
    // Refresh everything that depends on quantities, without rebuilding the inputs being typed in.
    function refreshQty() {
      const set = (sel, v) => { const el = root.querySelector(sel); if (el) el.textContent = v; };
      set('[data-cq-units]', totalQty());
      set('[data-cq-summary]', chosen().map((c) => `${c.v.color} × ${c.n}`).join(', '));
      const tt = $('.cc-total'); if (tt) tt.textContent = priced ? money(total()) : '+' + money(p.extraAreaPrice * extraCount() * totalQty());
      root.querySelectorAll('.cc-cq-row').forEach((row, i) => {
        const n = qtyMap()[i] || 0; row.classList.toggle('is-on', n > 0);
        const minus = row.querySelector('[data-d="-1"]'); if (minus) minus.disabled = !n || p.variants[i].available === false;
      });
      renderBulk(); renderNav();
    }
    function setQty(i, n) {
      if (!Object.values(S.qtyBy).some((x) => x > 0)) S.qtyBy = { [S.v]: Math.max(1, S.qty) };
      S.qtyBy[i] = Math.max(0, Math.min(500, Math.round(n) || 0));
      // always keep at least one unit in the order
      if (totalQty() === 0 || !Object.values(S.qtyBy).some((x) => x > 0)) S.qtyBy[i] = 1;
    }

    /* ---------- bulk discount progress ---------- */
    function bulkTier(q) { let t = null; TIERS.forEach((x) => { if (q >= x[0]) t = x; }); return t; }
    function bulkHtml() {
      const q = totalQty(), cur = bulkTier(q), next = TIERS.find((x) => q < x[0]);
      const top = TIERS[TIERS.length - 1][0];
      const pct = (n) => Math.min(100, (n / top) * 100);
      let msg;
      if (!cur) msg = `Add <strong>${next[0] - q}</strong> more to save <strong>${next[1]}%</strong> on this order.`;
      else if (next) msg = `You're saving <strong>${cur[1]}%</strong>. Add <strong>${next[0] - q}</strong> more to save <strong>${next[1]}%</strong>.`;
      else msg = `You're getting our best bulk price: <strong>${cur[1]}% off</strong>.`;
      const est = priced && cur ? `<p class="cc-bulk-est">Estimated total with your bulk discount: <s>${money(total())}</s> <strong>${money(Math.round(total() * (1 - cur[1] / 100)))}</strong><br><span>Applied automatically in your cart.</span></p>` : '';
      return `
        <p class="cc-bulk-title">Bulk discount</p>
        <p class="cc-bulk-msg">${msg}</p>
        <div class="cc-bulk-bar" role="progressbar" aria-label="Progress to the next bulk discount" aria-valuemin="0" aria-valuemax="${top}" aria-valuenow="${Math.min(q, top)}">
          <div class="cc-bulk-fill" style="width:${pct(q)}%"></div>
          ${TIERS.map((x) => `<span class="cc-bulk-mark ${q >= x[0] ? 'is-hit' : ''}" style="left:${pct(x[0])}%"></span>`).join('')}
        </div>
        <div class="cc-bulk-tiers">
          ${TIERS.map((x, i) => `<button type="button" data-act="bulk-qty" data-q="${x[0]}" aria-pressed="${cur === x}">
            <span class="cc-bulk-q">${x[0]}+</span><span class="cc-bulk-off">${x[1]}% off</span></button>`).join('')}
        </div>
        ${est}`;
    }
    function renderBulk() {
      const el = root.querySelector('[data-bulk]'); if (el) el.innerHTML = bulkHtml();
      const dl = root.querySelector('[data-dealer]'); if (dl) dl.innerHTML = dealerHtml();
    }

    /* ---------- dealer minimum (review step, dealers only) ----------
       Dealer orders need DEALER_MIN customized units in the cart in total, all designs combined. This shows the count
       (already in the cart + this design); the cart itself holds checkout until the minimum is met
       (cling-everywhere.js). Adding this design is always allowed: the rest can come from more designs. */
    let cartUnits = null, cartLoading = false;
    function loadCartUnits() {
      if (!DEALER || cartLoading) return;
      if (REP) { cartUnits = RS.order && DEALER ? RS.order.units : 0; renderBulk(); return; }
      cartLoading = true;
      const rootUrl = (global.Shopify && global.Shopify.routes && global.Shopify.routes.root) || '/';
      const ids = new Set((CZ.variants || []).map((v) => String(v[0])));
      fetch(rootUrl + 'cart.js', { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then((c) => {
          cartUnits = (c.items || []).reduce((n, it) => n + ((ids.size ? ids.has(String(it.variant_id))
            : it.properties && it.properties._proof_id && !it.properties._engraving_fee) ? it.quantity : 0), 0);
        })
        .catch(() => { cartUnits = 0; })
        .then(() => { cartLoading = false; renderBulk(); });
    }
    function dealerHtml() {
      if (cartUnits == null) setTimeout(loadCartUnits, 0);
      const here = S.repAdded ? 0 : totalQty(), sum = (cartUnits || 0) + here, left = Math.max(0, DEALER_MIN - sum); // a design already added to the rep's order counts once
      const pct = Math.min(100, (sum / DEALER_MIN) * 100);
      const s = (n) => (n === 1 ? '' : 's');
      return `
        <p class="cc-bulk-title">Dealer minimum</p>
        <p class="cc-bulk-msg">Dealer orders need at least <strong>${DEALER_MIN}</strong> customized units in total. Every customizable product, design and color counts toward it.</p>
        <div class="cc-bulk-bar" role="progressbar" aria-label="Customized units toward the dealer minimum" aria-valuemin="0" aria-valuemax="${DEALER_MIN}" aria-valuenow="${Math.min(sum, DEALER_MIN)}">
          <div class="cc-bulk-fill" style="width:${pct}%"></div>
        </div>
        ${cartUnits == null ? '<p class="cc-hint">Checking your cart…</p>' : `
        <p class="cc-dealer-count">${cartUnits} in ${REP ? 'the order' : 'your cart'} + ${here} in this design = <strong>${sum} of ${DEALER_MIN}</strong></p>
        <p class="cc-dealer-status${left ? '' : ' is-met'}">${left
          ? `Add ${left} more customized unit${s(left)} before ${REP ? 'placing the order' : 'checking out'}. You can add this design now and add more designs after.`
          : REP ? 'Minimum reached: you can place the order once this is added.' : 'Minimum reached: you can check out once this is in your cart.'}</p>`}`;
    }

    function renderNav() {
      const back = S.step > 1 ? `<button type="button" class="${btn2()}" data-act="back">Back to design</button>` : '<span></span>';
      const next = S.step === 1
        ? `<button type="button" class="${btn()}" data-act="next">Next: review your proof</button>`
        : `<button type="button" class="${btn('cc-btn-primary')}" data-act="cart" ${S.approved && !S.busy && !S.repAdded ? '' : 'disabled'}>
            ${S.busy ? 'Saving your proof…' : S.repAdded ? 'Added to the order' : REP ? (RS.ctx ? `Add to ${esc(RS.ctx.name)}'s order${priced ? `, ${money(total())}` : ''}` : 'Choose who the order is for at the top')
              : priced ? `Add to cart, ${money(total())}` : 'Add to cart'}</button>`;
      const nav = $('.cc-nav');
      nav.innerHTML = (S.step > 1 ? back : '') + next;
      nav.classList.toggle('cc-nav-single', S.step === 1); // a lone button sits centered
      $('.cc-step-error').textContent = S.stepError;
    }

    async function paintProofs() {
      for (const c of root.querySelectorAll('canvas[data-proof]')) {
        const vv = c.dataset.vi != null ? p.variants[+c.dataset.vi] : variant();
        const img = await imageFor(vv);
        const x = c.getContext('2d'), sc = dpr * 0.5;
        x.setTransform(sc, 0, 0, sc, 0, 0);
        paintScene(x, c.dataset.proof, sc, false, img, null, vv);
      }
    }

    function render() {
      root.classList.toggle('cc-editing', S.step === 1);
      requestAnimationFrame(() => { placeTour(); measurePinChrome(); });
      renderMTools(); renderSheet();
      renderAdjustControls();
      renderChrome();
      $('.cc-body').innerHTML = S.step === 1 ? viewDesign() : viewReview();
      renderNav();
      renderLive();
      if (S.step === 2) paintProofs();
      draw();
    }

    /* ---------- actions ---------- */
    function goStep(n) {
      S.stepError = '';
      if (n === 2 && S.step === 1) {
        if (!engraved().length) {
          S.stepError = cur().type === 'logo' && !cur().src ? 'Upload your logo, or choose another option, to continue.' : 'Add a design to at least one area to continue.';
          return renderNav();
        }
        const bad = engraved().find(hasErrors);
        if (bad) {
          S.area = bad;
          S.stepError = multi ? `The ${areaOf(bad).label.toLowerCase()} needs attention before you can review.` : 'Your design needs attention before you can review.';
          return render();
        }
      }
      S.step = n; S.maxStep = Math.max(S.maxStep, n);
      if (n === 2) S.area = engraved()[0];
      render();
      if (root.getBoundingClientRect().top < 0) root.scrollIntoView({ block: 'start' });
    }

    // Plain-language summary of how an upload was turned into white engraving.
    function conversionNote(d) {
      const an = d.an, parts = [];
      if (d.mode === 'photo') return 'This looks like a photo, so it will engrave as a fine white dot pattern. Choose Logo or graphic if it isn’t a photo.';
      const cu = an.cleanup || {};
      if (cu.artboard) parts.push('This looks like a screenshot, so we kept just the design on the artboard and left out the window around it.');
      if (d.src && d.src.kind === 'pdf') parts.push(`We read your ${/\.ai$/i.test(d.fileName) ? 'Illustrator' : 'PDF'} file from its vector artwork${d.src.pages > 1 ? ' (page 1)' : ''}.`);
      if (d.src && d.src.kind === 'postscript') parts.push('We read your Illustrator file from its vector artwork.');
      if (an.bgType === 'alpha') parts.push('Transparent areas stay clear.');
      if (an.bgType === 'color') parts.push(`We removed the ${bgName(an.bg)} background${cu.frames ? ` and the border${cu.frames > 1 ? 's' : ''} drawn around your file` : ''}.`);
      else if (cu.frames) parts.push(`We removed the border${cu.frames > 1 ? 's' : ''} drawn around your file.`);
      if (cu.trimmed) parts.push('We trimmed the empty space so your logo fills the design area.');
      if (!an.flat) parts.push('Its gradients and shading engrave as one solid white shape. Choose Photo for a shaded look instead.');
      else if (an.linework && an.lineCut != null && d.colorsOn.every((v, i) => v === an.defaultOn[i])) {
        parts.push('It\'s a shaded drawing, so its outlines and darker shading engrave white and the lighter colors stay clear. Use Line detail to keep more or less of the shading.');
      }
      else if (an.linework && d.colorsOn.every((v, i) => v === an.defaultOn[i])) {
        parts.push('Its dark line work engraves white and the colored areas stay clear. You can choose which colors engrave.');
      }
      else if (an.palette.length > 1) {
        const on = d.colorsOn.filter(Boolean).length;
        const asDetected = an.lightOnDark && d.colorsOn.every((v, i) => v === an.defaultOn[i]);
        if (asDetected) parts.push('Its light linework engraves white and the dark fill stays clear, the way a badge engraves best. You can choose which colors engrave.');
        else
        if (on === an.palette.length) parts.push(`Its ${on} colors all engrave white${d.gap ? ', with a thin gap where they touch so the details stay visible' : ''}.`);
        else if (on) parts.push(`${on} of its ${an.palette.length} colors engrave white; the others stay clear.`);
      }
      else parts.push('Everything visible engraves white.');
      if (!(d.src && d.src.vector)) parts.push('For finer detail in your engraving, upload a higher-resolution image (at least 1000 px wide) or a vector file like an SVG, PDF or AI.');
      return parts.join(' ');
    }
    function bgName(rgb) {
      const l = (rgb[0] * 0.299 + rgb[1] * 0.587 + rgb[2] * 0.114);
      const sat = Math.max(...rgb) - Math.min(...rgb);
      return sat < 24 ? (l > 225 ? 'white' : l < 40 ? 'black' : 'gray') : 'solid-color';
    }
    async function handleFile(file, fromLibrary) {
      const d = cur();
      d.error = ''; d.note = '';
      if (!file) return;
      if (!fromLibrary) Object.assign(d, { libraryId: null, libraryName: '' });
      if (file.size > MAX_FILE) { d.error = 'That file is over 20 MB. Export a smaller PNG or an SVG.'; return render(); }
      if (!/^(image\/(png|jpeg|webp|svg\+xml)|application\/(pdf|postscript|illustrator))$/.test(file.type) && !/\.(png|jpe?g|webp|svg|pdf|ai|eps)$/i.test(file.name)) {
        d.error = 'Use a PNG, JPG, WEBP, SVG, PDF or Adobe Illustrator (AI, EPS) file.';
        return render();
      }
      S.busy = true; render();
      let opened = false;
      try {
        // Keep an in-memory copy of the customer's file right away, so it can still be read at checkout even
        // if they move, rename or edit the original on their computer in the meantime.
        file = new File([await file.arrayBuffer()], file.name, { type: file.type, lastModified: file.lastModified });
        const src = await E.loadFile(file);
        const thumb = src.canvas.toDataURL('image/png');
        // Isolate the real artwork: drop frames/borders and specks, trim empty margins, then analyze what's left.
        const first = E.analyzeLogo(src.canvas);
        const iso = E.isolateArtwork(src.canvas, first);
        if (iso.canvas !== src.canvas) {
          const k = src.naturalW / src.canvas.width;
          Object.assign(src, { canvas: iso.canvas, naturalW: Math.round(iso.canvas.width * k), naturalH: Math.round(iso.canvas.height * k) });
        }
        const an = iso.canvas === src.canvas && !iso.frames && !iso.trimmed ? first : E.analyzeLogo(src.canvas);
        an.cleanup = { frames: iso.frames, specks: iso.specks, trimmed: iso.trimmed, artboard: !!iso.artboard };
        Object.assign(d, { src, file, fileName: file.name, an, invert: false, mode: an.photoLike ? 'photo' : 'logo', bright: 0,
          colorsOn: (an.defaultOn || an.palette.map(() => true)).slice(), gap: an.palette.length > 1 && !an.linework, bgTol: 28, lineShift: 0, _lbl: null, _white: null,
          thumb, cx: 0.5, cy: 0.5, scale: 0.8, rot: 0 });
        d.auto = { mode: d.mode, colorsOn: d.colorsOn.slice(), gap: d.gap, bgTol: d.bgTol, bright: d.bright, invert: d.invert, lineShift: 0 };
        opened = true;
        d._rev++;
        fitLogoInside(S.area);
        d.note = conversionNote(d);

      } catch (e) {
        d.error = e.message || "We couldn't read that file.";
      }
      S.busy = false; render();
      if (opened) openAdjust();
      return opened;
    }

    /* ---------- dealers: their company's saved artwork ----------
       Listed by the proof server through the store's app proxy, which tells it who's logged in, so a dealer only
       ever sees their own company's artwork. Picking one loads the original file exactly like an upload. */
    const libraryBase = () => (cfg.uploadUrl ? String(cfg.uploadUrl).replace(/\/upload(\?.*)?$/, '') : '');
    // the server's links are paths on the store; resolve them against the address the customizer uploads to
    const libUrl = (u) => { try { return new URL(u, new URL(libraryBase() || '/', global.location.href)).href; } catch (e) { return u; } };
    function loadLibrary() {
      if (S.library || !DEALER || !libraryBase()) { if (!S.library) { S.library = { items: [] }; render(); } return; }
      S.library = 'loading';
      // the dealer's signed company pass (snippets/cling-everywhere.liquid) links them to their company's library
      const pass = REP && RS.ctx && RS.ctx.kind === 'company' ? `?rep_target=${encodeURIComponent(RS.target)}`
        : CZ.libraryCompany && CZ.librarySig ? `?company=${encodeURIComponent(CZ.libraryCompany)}&company_sig=${encodeURIComponent(CZ.librarySig)}` : '';
      fetch(libraryBase() + '/library' + pass, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
        .then((r) => (r.ok ? r.json() : { items: [] }))
        .then((j) => { S.library = { company: j.company || null, items: Array.isArray(j.items) ? j.items : [] }; })
        .catch(() => { S.library = { items: [], error: true }; })
        .then(() => render());
    }
    async function pickLibrary(id) {
      const d = cur(), it = S.library && S.library.items && S.library.items.find((x) => x.id === id);
      if (!it || S.busy) return;
      S.busy = true; render();
      try {
        const r = await fetch(libUrl(it.file), { credentials: 'same-origin' });
        if (!r.ok) throw new Error('load');
        const blob = await r.blob();
        const file = new File([blob], it.fileName || it.name, { type: blob.type });
        S.busy = false;
        d._prevType = 'onfile'; d.type = 'logo';
        const opened = await handleFile(file, true);
        if (opened) { Object.assign(d, { rights: true, libraryId: it.id, libraryName: it.name }); render(); }
        else d.type = 'onfile', render();
      } catch (e) {
        S.busy = false; d.type = 'onfile';
        d.error = "We couldn't load that artwork. Try again, or describe it below and our team will use it.";
        render();
      }
    }

    root.addEventListener('click', (e) => {
      const b = e.target.closest('[data-act]');
      if (!b || b.disabled || !root.contains(b)) return;
      const d = adj.open && adjustDlg.contains(b) ? adjDesign() : cur();
      // a choice made in a phone menu closes that menu once it's applied (the preview shows the result)
      const fromSheet = !!(S.sheet && b.closest('.cc-sheet') && ['color', 'area', 'type'].includes(b.dataset.act));
      let focusLine1 = false;
      switch (b.dataset.act) {
        case 'next': return goStep(S.step + 1);
        case 'back': return goStep(S.step - 1);
        case 'goto': return goStep(+b.dataset.step);
        case 'color': S.v = +b.dataset.i; syncUrl(); break;
        case 'area': S.area = b.dataset.area; S.layer = 'main'; break;
        case 'view': { const first = p.areas.find((a) => a.view === b.dataset.view); if (first) S.area = first.key; break; }
        case 'type': {
          const t = b.dataset.type;
          d.error = '';
          if (t.startsWith('art:')) {
            const was = d.type === 'art' ? d.artId : null;
            d.type = 'art'; d.artId = t.slice(4);
            if (was !== d.artId) { d.flip = defaultFlip(d.artId, curArea()); Object.assign(d, { cx: 0.5, cy: 0.5, scale: 0.85, rot: 0 }); }
          } else {
            if (d.type === 'art') d.flip = false;
            d.type = t;
          }
          if (d.type !== 'logo') d.note = '';
          focusLine1 = t === 'text';
          break;
        }
        case 'mode': d.mode = b.dataset.mode; if (d.an) d.note = conversionNote(d); break;
        case 'logo-color': d.colorsOn[+b.dataset.i] = !(d.colorsOn[+b.dataset.i] !== false); break;
        case 'font': d.font = b.dataset.font; break;
        case 'monostyle': d.monoStyle = b.dataset.style; break;
        case 'art':
          if (d.artId !== b.dataset.id) { d.artId = b.dataset.id; d.flip = defaultFlip(d.artId, curArea()); Object.assign(d, { cx: 0.5, cy: 0.5, scale: 0.85, rot: 0 }); }
          break;
        case 'remove-file': Object.assign(d, { src: null, file: null, fileName: '', note: '', error: '', rights: false, libraryId: null, libraryName: '' }); d._rev++; break;
        case 'lib-pick': pickLibrary(b.dataset.id); return;
        case 'lib-back': d._prevType = 'logo'; d.type = 'onfile'; d.error = ''; d._cache = null; break;
        case 'center': { const t = target(); t.cx = 0.5; if (t === d) t.cy = 0.5; break; }
        case 'straighten': target().rot = 0; break;
        case 'text-add': {
          d.textLayer = { lines: ['', '', ''], font: 'Oswald', cx: 0.5, cy: 0.82, scale: 0.56, rot: 0 };
          // make room: if the logo hasn't been moved yet, lift it and shrink it a little
          if (d.cx === 0.5 && d.cy === 0.5 && d.rot === 0) { d.cy = 0.38; d.scale = Math.min(d.scale, 0.62); d._autoLayout = true; }
          S.layer = 'text';
          autoLayout(d);
          render();
          focusField('[data-bind="tline0"]'); // same tap: opens the keyboard on phones
          return;
        }
        case 'text-remove': d.textLayer = null; S.layer = 'main'; break;
        case 'tfont': if (d.textLayer) { d.textLayer.font = b.dataset.font; if (d._autoLayout) autoLayout(d); else keepTextInside(d); } break;
        case 'layer': S.layer = b.dataset.layer; break;
        case 'cart': return addToCart();
        case 'bulk-qty': {
          // top up the color they designed on so the order reaches this tier
          const tier = +b.dataset.q, pi = primaryIndex(), others = totalQty() - (qtyMap()[pi] || 0);
          setQty(pi, Math.max(qtyMap()[pi] || 0, tier - others));
          render();
          const again = root.querySelector(`[data-act="bulk-qty"][data-q="${tier}"]`); if (again) again.focus();
          return;
        }
        case 'cq-step': {
          const i = +b.dataset.i, d = +b.dataset.d;
          setQty(i, (qtyMap()[i] || 0) + d);
          render();
          const again = root.querySelector(`[data-act="cq-step"][data-i="${i}"][data-d="${d}"]`);
          if (again && !again.disabled) again.focus(); else { const inp = root.querySelector(`[data-cq="${i}"]`); if (inp) inp.focus(); }
          return;
        }
        case 'tour': return startTour(true);
        case 'sheet': return openSheet(b.dataset.sheet);
        case 'sheet-close': return closeSheet();
        case 'download-proof': return downloadProof(b);
        case 'adjust-open': return openAdjust();
        case 'adjust-done': return closeAdjust(false);
        case 'adjust-cancel': return closeAdjust(true);
        case 'adjust-zoom': { const z = +b.dataset.z; applyZoom(adj.zoom, z); adj.zoom = z; return; }
        case 'adjust-bg':
          adj.bg = b.dataset.bg;
          adjustDlg.querySelectorAll('[data-act="adjust-bg"]').forEach((x) => x.setAttribute('aria-pressed', String(x.dataset.bg === adj.bg)));
          return paintAdjust();
        default: return;
      }
      S.stepError = '';
      render();
      // Everything below runs within the same tap, which is what lets phones (iPhones especially) open the keyboard.
      if (fromSheet) { S.sheet = null; renderSheet(); }
      if (focusLine1) focusField('[data-bind="line0"]');
      else if (fromSheet) { const tb = root.querySelector(`[data-sheet="${b.dataset.act === 'type' ? 'design' : b.dataset.act}"]`); if (tb) tb.focus({ preventScroll: true }); }
    });
    // Focus a field (opening the keyboard on phones) and scroll it into view just below the pinned preview.
    function focusField(sel) {
      const inp = root.querySelector(sel); if (!inp) return;
      inp.focus({ preventScroll: true });
      const wrap = root.querySelector('.cc-stage-wrap');
      const pinned = wrap && getComputedStyle(wrap).position === 'sticky';
      // scrolling can pin the preview, which moves everything; re-measure until the field settles under it
      const field = inp.closest('.cc-field') || inp; // bring the field's label into view too
      for (let i = 0; i < 4; i++) {
        const r = field.getBoundingClientRect(), below = pinned ? wrap.getBoundingClientRect().bottom : 0;
        const want = below + 16, delta = r.top - want;
        if (Math.abs(delta) < 4 || (delta > 0 && r.bottom <= window.innerHeight * 0.45)) break;
        const before = window.scrollY;
        window.scrollBy(0, delta);
        if (window.scrollY === before) break; // can't scroll any further
      }
    }
    function syncUrl() {
      // Keep ?variant= in sync like a normal Shopify product page, so links and refreshes keep the color.
      if (cfg.mode !== 'shopify' || !global.history || !history.replaceState) return;
      const u = new URL(global.location.href); u.searchParams.set('variant', variant().id);
      history.replaceState(history.state, '', u);
    }

    root.addEventListener('input', (e) => {
      if (e.target.dataset && e.target.dataset.cq != null) { setQty(+e.target.dataset.cq, +e.target.value); return refreshQty(); }
      const t = e.target, b = t.dataset && t.dataset.bind;
      if (!b) return;
      const d = cur();
      const val = t.type === 'checkbox' ? t.checked : t.type === 'range' || t.type === 'number' ? +t.value : t.value;
      if (b === 'qty') { S.qty = Math.max(1, Math.min(500, Math.round(val) || 1)); return refreshQty(); }
      if (b === 'notes') { S.notes = val; return; }
      if (b === 'onfile') {
        if (val) {
          if (d.type !== 'onfile') d._prevType = d.type;
          d.type = 'onfile'; Object.assign(d, { cx: 0.5, cy: 0.5, scale: 0.7, rot: 0 });
        } else d.type = d._prevType && d._prevType !== 'onfile' ? d._prevType : 'logo';
        d.error = ''; d._cache = null;
        return render();
      }
      if (b === 'approved') { S.approved = val; t.closest('.cc-approve').classList.toggle('is-approved', val); return renderNav(); }
      if (b.startsWith('tline')) { if (d.textLayer) { d.textLayer.lines[+b.slice(5)] = val; if (d._autoLayout) autoLayout(d); else keepTextInside(d); } }
      else if (/^line\d$/.test(b)) d.lines[+b.slice(4)] = val;
      else if (b.startsWith('mono.')) { d.mono[b.slice(5)] = val.toUpperCase().replace(/[^A-Z]/g, ''); t.value = d.mono[b.slice(5)]; }
      else if (b === 'flip' && t.hasAttribute('data-artflip')) {
        const def = defaultFlip(d.artId, curArea());
        d.flip = val ? !def : def;
        const h = root.querySelector('.cc-controls .cc-hint');
        if (h) h.textContent = `The ${artOf(d.artId).label} faces ${artFacing(d)}` + (val ? '.' : `, toward the center of your ${p.title}.`);
      }
      else if (b === 'scalePct') { d._autoLayout = false; target().scale = val / 100; }
      else if (b === 'rot') { target().rot = val; const o = root.querySelector('.cc-rot-val'); if (o) o.textContent = `${val}°`; }
      else d[b] = val;
      draw();
      renderLive();
    });
    root.addEventListener('change', (e) => {
      if (e.target.matches('[data-file]')) return handleFile(e.target.files[0]);
      if (e.target.dataset.bind === 'qty') e.target.value = S.qty;
      // after typing a quantity, refresh the list and the proofs (a color may have been added or removed)
      if (e.target.dataset.cq != null) { const i = e.target.dataset.cq; render(); const inp = root.querySelector(`[data-cq="${i}"]`); if (inp) inp.focus(); }
    });
    root.addEventListener('dragover', (e) => { const d = e.target.closest('[data-drop]'); if (d) { e.preventDefault(); d.classList.add('is-over'); } });
    root.addEventListener('dragleave', (e) => { const d = e.target.closest('[data-drop]'); if (d) d.classList.remove('is-over'); });
    root.addEventListener('drop', (e) => { const d = e.target.closest('[data-drop]'); if (d) { e.preventDefault(); handleFile(e.dataTransfer.files[0]); } });

    /* ---------- proof package ---------- */
    /* ---------- downloadable proof ----------
       One PNG with the design on every chosen color, what's engraved, colors and quantities, and the date,
       made in the browser so the customer can keep, print or share it. */
    async function downloadProof(btn) {
      const label = btn.textContent;
      btn.disabled = true; btn.textContent = 'Preparing your proof…';
      try {
        const ks = engraved(), views = p.views.filter((vw) => p.areas.some((a) => a.view === vw.key && ks.includes(a.key)));
        const tiles = [];
        for (const c of chosen()) {
          const img = await imageFor(c.v);
          for (const vw of views) {
            const sc = 2, t = E.makeCanvas(STAGE * sc, SH * sc), x = t.getContext('2d');
            x.setTransform(sc, 0, 0, sc, 0, 0);
            paintScene(x, vw.key, sc, false, img, null, c.v);
            tiles.push({ canvas: t, caption: c.v.color + (views.length > 1 ? `, ${vw.label}` : '') + `  ×${c.n}` });
          }
        }
        // layout: up to 2 tiles per row
        const W = 1800, M = 80, gap = 40, cols = tiles.length > 1 ? 2 : 1;
        const tw = (W - M * 2 - gap * (cols - 1)) / cols, th = tw * (SH / STAGE), rows = Math.ceil(tiles.length / cols);
        const fam = getComputedStyle(root).fontFamily || 'sans-serif';
        const lines = [
          ...ks.map((k) => (multi ? `${areaOf(k).label}: ` : 'Engraving: ') + describe(k)),
          `Colors: ${chosen().map((c) => `${c.v.color} × ${c.n}`).join(', ')}`,
          ...(S.notes.trim() ? [`Notes: ${S.notes.trim()}`] : []),
        ];
        const headH = 230, detailH = 90 + lines.length * 46, footH = 110;
        const H = headH + rows * (th + 70) + detailH + footH;
        const cv = E.makeCanvas(W, H), x = cv.getContext('2d');
        x.fillStyle = '#ffffff'; x.fillRect(0, 0, W, H);
        x.fillStyle = '#121212'; x.textBaseline = 'alphabetic';
        x.font = `400 64px ${fam}`; x.fillText(p.title.toUpperCase(), M, 120);
        x.font = `400 30px ${fam}`; x.fillStyle = '#666';
        x.fillText(`Engraving proof · ${new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}`, M, 172);
        tiles.forEach((t, i) => {
          const cx = M + (i % cols) * (tw + gap), cy = headH + Math.floor(i / cols) * (th + 70);
          x.fillStyle = '#f4f4f4'; roundRectPath(x, cx, cy, tw, th, 16); x.fill();
          x.drawImage(t.canvas, cx, cy, tw, th);
          x.fillStyle = '#121212'; x.font = `400 30px ${fam}`; x.fillText(t.caption.toUpperCase(), cx, cy + th + 46);
        });
        let y = headH + rows * (th + 70) + 64;
        x.fillStyle = '#121212'; x.font = `400 32px ${fam}`;
        lines.forEach((ln) => { x.fillText(fitText(x, ln, W - M * 2), M, y); y += 46; });
        x.fillStyle = '#777'; x.font = `400 26px ${fam}`;
        x.fillText('Laser engraving leaves a bright white, permanent mark. Product colors may look slightly different on screen.', M, H - 50);
        const blob = await new Promise((res) => cv.toBlob(res, 'image/png'));
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `cling-proof-${(p.handle || p.render || 'design').replace(/[^a-z0-9-]+/gi, '-').toLowerCase()}-${new Date().toISOString().slice(0, 10)}.png`;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(a.href), 4000);
      } catch (e) {
        S.stepError = "We couldn't make the proof image. Please try again."; renderNav();
      } finally {
        btn.disabled = false; btn.textContent = label;
      }
    }
    function roundRectPath(x, l, t, w, h, r) {
      x.beginPath(); x.moveTo(l + r, t); x.arcTo(l + w, t, l + w, t + h, r); x.arcTo(l + w, t + h, l, t + h, r);
      x.arcTo(l, t + h, l, t, r); x.arcTo(l, t, l + w, t, r); x.closePath();
    }
    // shorten a line with an ellipsis if it's wider than the space
    function fitText(x, text, max) {
      if (x.measureText(text).width <= max) return text;
      let t = text; while (t.length > 1 && x.measureText(t + '…').width > max) t = t.slice(0, -1);
      return t + '…';
    }

    function summaryFor(k) {
      const d = S.designs[k], pre = multi ? areaOf(k).label + ': ' : '';
      const tx = hasTextLayer(d) && textLines(d.textLayer) ? ` + "${d.textLayer.lines.filter((l) => l.trim()).join(' / ')}"` : '';
      if (d.type === 'art') return (isFitted(d) ? `${pre}${artOf(d.artId).label}` : `${pre}${artOf(d.artId).label} (facing ${artFacing(d)})`) + tx;
      if (d.type === 'logo') return `${pre}Logo (${d.fileName})${d.flip ? ' (flipped)' : ''}${tx}`;
      if (d.type === 'text') return `${pre}"${d.lines.filter((l) => l.trim()).join(' / ')}"`;
      if (d.type === 'mono') return `${pre}Monogram ${[d.mono.first, d.mono.last, d.mono.middle].filter(Boolean).join('')}`;
      if (d.type === 'onfile') return `${pre}Artwork on file` + (d.onFileNote && d.onFileNote.trim() ? ` (${d.onFileNote.trim()})` : '');
      return null;
    }
    function buildSpec() {
      const v = variant(), areas = {};
      p.areas.forEach((a) => {
        const d = S.designs[a.key], sz = areaIn(a);
        const o = { label: a.label, view: a.view, type: isEngraved(a.key) ? d.type : 'none', widthIn: +sz.w.toFixed(3), heightIn: +sz.h.toFixed(3), cornerRadiusIn: +(a.face.r * Math.min(sz.w, sz.h)).toFixed(3) };
        if (o.type === 'none') { areas[a.key] = o; return; }
        if (d.type !== 'none') Object.assign(o, {
          placement: { centerXPct: +(d.cx * 100).toFixed(1), centerYPct: +(d.cy * 100).toFixed(1), sizePct: Math.round(d.scale * 100), rotationDeg: Math.round(d.rot) },
          charged: engraved().indexOf(a.key) > 0 && p.extraAreaPrice > 0,
        });
        if (d.type === 'logo') Object.assign(o, {
          file: d.fileName, mode: d.mode, flipped: !!d.flip, rightsConfirmed: d.rights, libraryId: d.libraryId || undefined,
          conversion: d.mode === 'photo' ? { style: 'dot pattern', brightness: d.bright, swapped: d.invert } : {
            style: 'all visible artwork white, background and holes clear',
            background: d.an.bgType === 'alpha' ? 'transparent' : d.an.bgType === 'color' ? `${d.an.bgHex} removed (tolerance ${d.bgTol})` : 'none',
            cleanup: d.an.cleanup || null,
            colors: d.an.palette.map((c, i) => ({ color: c.hex, engraved: d.colorsOn[i] !== false })),
            gradient: !d.an.flat, gapBetweenColorsIn: d.gap && d.colorsOn.filter(Boolean).length > 1 ? COLOR_GAP_IN : 0,
          },
          // what the converter chose by itself vs. what the customer ended up with: any difference means the
          // automatic result needed fixing, which makes this file a candidate for improving the converter
          autoConversion: d.auto || null,
          adjustedByCustomer: d.auto ? ['mode', 'gap', 'bgTol', 'bright', 'invert', 'lineShift'].filter((k) => (d.auto[k] || 0) !== (d[k] || 0))
            .concat(d.auto.colorsOn.some((v, i) => v !== d.colorsOn[i]) ? ['colors'] : []) : [],
        });
        if (hasTextLayer(d) && textLines(d.textLayer)) {
          const t = d.textLayer;
          o.text = { lines: t.lines.filter((l) => l.trim()), font: t.font,
            placement: { centerXPct: +(t.cx * 100).toFixed(1), centerYPct: +(t.cy * 100).toFixed(1), sizePct: Math.round(t.scale * 100), rotationDeg: Math.round(t.rot) } };
        }
        if (d.type === 'art') Object.assign(o, isFitted(d)
          ? { art: d.artId, artName: artOf(d.artId).label, placement: { fit: 'Fills the panel, centered' } }
          : { art: d.artId, artName: artOf(d.artId).label, facing: artFacing(d), flipped: !!d.flip });
        if (d.type === 'text') Object.assign(o, { lines: d.lines.filter((l) => l.trim()), font: d.font });
        if (d.type === 'mono') Object.assign(o, { initials: { ...d.mono }, style: d.monoStyle });
        if (d.type === 'onfile') { delete o.placement; o.onFile = { note: (d.onFileNote || '').trim() }; }
        areas[a.key] = o;
      });
      const now = new Date().toISOString();
      return {
        version: 3, createdAt: now,
        product: { id: p.id, title: p.title, render: p.render, bodyWidthIn: p.widthIn, bodyHeightIn: p.heightIn },
        variant: { id: p.variants[primaryIndex()].id, color: p.variants[primaryIndex()].color }, quantity: totalQty(),
        colors: chosen().map((c) => ({ variantId: c.v.id, color: c.v.color, quantity: c.n })),
        production: { dpi: Object.fromEntries(engraved().map((k) => [k, areaDpi(k)])), note: 'Black = engrave. Each production file is one engraving area at actual size, rounded corners already applied.' },
        areas, notes: S.notes.trim(), approvedByCustomer: true, approvedAt: now,
        pricing: { unitCents: priced ? v.price : null, extraAreas: extraCount(), extraAreaCents: p.extraAreaPrice, currency: cfg.currency },
      };
    }
    const areaDpi = (k) => (S.designs[k].type === 'logo' && S.designs[k].mode === 'photo' ? PHOTO_DPI : DPI);
    // a small PNG (at most 320 px) of the customer's original logo, from the preview made when it was opened

    async function buildFiles() {
      const ks = engraved(), files = {}, canvases = {};
      const pv0 = p.variants[primaryIndex()]; // a color that's actually being ordered
      const img = await imageFor(pv0);
      const viewsUsed = p.views.filter((vw) => p.areas.some((a) => a.view === vw.key && ks.includes(a.key)));
      const pv = E.makeCanvas(STAGE * viewsUsed.length, SH), px = pv.getContext('2d');
      viewsUsed.forEach((vw, i) => { px.save(); px.translate(i * STAGE, 0); paintScene(px, vw.key, 1, false, img, null, pv0); px.restore(); });
      // where each engraved area sits in the proof image, so the server can check placement against the laser file
      const geom = { width: pv.width, height: pv.height, areas: {} };
      viewsUsed.forEach((vw, i) => ks.filter((k) => areaOf(k).view === vw.key).forEach((k) => {
        const f = faceRect(areaOf(k)); geom.areas[k] = { x: f.x + i * STAGE, y: f.y, w: f.w, h: f.h };
      }));
      files.preview = await E.canvasToPng(pv);
      for (const k of ks.filter((k2) => S.designs[k2].type !== 'onfile')) { // on file: our team supplies the laser file
        const dpi = areaDpi(k), sz = areaIn(areaOf(k)), W = Math.round(sz.w * dpi), H = Math.round(sz.h * dpi);
        const c = E.production(maskFor(k, W, H).mask, W, H);
        files['production_' + k] = await E.canvasToPng(c, dpi);
        canvases['production_' + k] = c;
      }
      p.areas.forEach((a) => (S.designs[a.key]._cache = null));
      return { files, canvases, geom };
    }

    async function addToCart() {
      if (REP && !(RS.target && RS.ctx)) { S.stepError = 'Choose who the order is for in the bar at the top first.'; renderNav(); return; }
      S.busy = true; S.stepError = ''; renderNav();
      let phase = 'prepare';
      try {
        const spec = buildSpec();
        const { files, canvases, geom } = await buildFiles();
        spec.preview = geom;
        draw();
        if (cfg.mode === 'demo') { S.busy = false; renderNav(); return showDemo(spec, canvases); }

        phase = 'upload';
        const fd = new FormData();
        fd.append('spec', JSON.stringify(spec));
        Object.entries(files).forEach(([k, blob]) => fd.append(k, blob, k + '.png'));
        engraved().forEach((k) => { const d = S.designs[k]; if (d.type === 'logo' && d.file) fd.append('original_' + k, d.file, d.file.name); });
        for (const k of engraved()) {
          const d = S.designs[k];
        }
        const up = await fetch(cfg.uploadUrl, { method: 'POST', body: fd });
        if (!up.ok) throw new Error('upload ' + up.status);
        const { proofId, previewUrl, dashboardUrl } = await up.json();

        phase = 'cart';
        const v = variant();
        const properties = { Engraving: engraved().map(summaryFor).join(', '), _proof_id: proofId, _proof_preview: previewUrl };
        // link for the Clingfishing team: shows on the order in the Shopify admin (hidden from customers by the "_")
        if (dashboardUrl) properties._Proof = dashboardUrl;
        if (S.notes.trim()) properties['Engraving notes'] = S.notes.trim();
        // one cart line per color, all sharing the same proof
        const items = chosen().map((c) => ({ id: c.v.id, quantity: c.n, properties }));
        if (p.extraAreaVariantId && extraCount()) {
          items.push({ id: p.extraAreaVariantId, quantity: totalQty() * extraCount(),
            properties: { For: `${p.title}, ${v.color}`, Areas: engraved().slice(1).map((k) => areaOf(k).label).join(', '), _proof_id: proofId, _engraving_fee: 'yes' } });
        }
        if (REP) {
          const j = await repPost('add', { contact: RS.contact, items });
          RS.order = j.order; RS.rates = null; RS.note = `Added to ${j.name}'s order.`; S.repAdded = true;
          markInProgress(true);
          S.busy = false; cartUnits = DEALER ? RS.order.units : 0;
          renderRepBar(); render(); openRepPanel();
          return;
        }
        const rootUrl = (global.Shopify && global.Shopify.routes && global.Shopify.routes.root) || '/';
        const res = await fetch(rootUrl + 'cart/add.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items }),
        });
        if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.description || 'cart ' + res.status); }
        global.location.href = rootUrl + 'cart';
      } catch (e) {
        console.error(e);
        S.busy = false;
        S.stepError = phase === 'cart'
          ? (REP ? `The proof was saved, but it couldn't be added to the order (${e.message}).` : `Your proof was saved, but it couldn't be added to the cart (${e.message}). Try again, or contact us.`)
          : phase === 'upload' ? 'Your proof could not be uploaded. Check your connection and try again.'
            : 'Your proof could not be prepared. Reload the page and try again.';
        renderNav();
      }
    }

    function showDemo(spec, canvases) {
      const dlg = $('.cc-dialog');
      const id = 'CF-' + new Date().toISOString().slice(2, 10).replace(/-/g, '') + '-' + Math.random().toString(36).slice(2, 6).toUpperCase();
      const v = variant(), ks = engraved();
      const slug = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      const folder = `01_${slug(p.title)}_${slug(v.color)}_${id}`;
      const pad = (s, n) => s + ' '.repeat(Math.max(1, n - s.length));
      const tree = [
        `orders/${new Date().toISOString().slice(0, 7)}/1001_demo-customer/`,
        `  ${pad('order.json', 26)}order details and status`,
        `  ${folder}/`,
        `    ${pad('spec.json', 30)}everything the customer chose`,
        `    ${pad('1001_01_proof_v1.png', 30)}the proof they approved`,
        ...ks.flatMap((k) => [
          `    ${pad(`1001_01_${k}_v1.svg`, 30)}${areaOf(k).label.toLowerCase()}: traced white vector`,
          `    ${pad(`1001_01_${k}_v1.png`, 30)}${areaOf(k).label.toLowerCase()}: laser file`,
        ]),
        ...ks.filter((k) => S.designs[k].type === 'logo').map((k) => `    ${pad(`1001_01_${k}_original${(S.designs[k].fileName.match(/\.[a-z0-9]+$/i) || ['.png'])[0].toLowerCase()}`, 30)}their untouched upload`),
        `    ${pad('1001_01_qa_v1.json', 30)}automatic proof checks`,
      ].join('\n');
      const charge = extraCount() && p.extraAreaPrice
        ? `<p class="cc-hint">The cart also gets ${extraCount() * totalQty()} × additional engraving area (${money(p.extraAreaPrice)} each), linked to this proof.</p>` : '';
      dlg.innerHTML = `
        <div class="cc-dialog-inner">
          <h2 class="cc-h ${esc(cfg.headingClass)}">Your team would receive this</h2>
          <p class="cc-hint">This is a demo, so nothing went to a cart. On your store, the customer goes to checkout and these files are filed under their order as soon as it's placed.</p>
          ${charge}
          <pre class="cc-tree">${esc(tree)}</pre>
          <h3 class="cc-h3">Laser files</h3>
          <p class="cc-hint">One file per engraving area. Black engraves. Each is the area's exact size at ${DPI} DPI (photos ${PHOTO_DPI} DPI), with its rounded corners already applied, so it drops into laser software at true size.</p>
          <div class="cc-prod-files">
            ${ks.map((k) => { const sz = areaIn(areaOf(k)); return `<figure><img alt="${esc(areaOf(k).label)} production file" src="${canvases['production_' + k].toDataURL('image/png')}"><figcaption>production_${k}.png, ${fmtIn(sz.w)} × ${fmtIn(sz.h)} in</figcaption></figure>`; }).join('')}
          </div>
          <h3 class="cc-h3">spec.json</h3>
          <pre class="cc-tree cc-json">${esc(JSON.stringify(spec, null, 2))}</pre>
          <div class="cc-nav"><span></span><button type="button" class="${btn()}" data-close>Close</button></div>
        </div>`;
      dlg.querySelector('[data-close]').onclick = () => dlg.close();
      dlg.showModal();
    }

    /* ---------- first-visit guided tour ----------
       Four short steps, each a black box with white text pointing at the part of the page it explains, with
       a highlight ring around that part. Shown once per browser; "How it works" replays it. */
    const TOUR_KEY = 'cling-customizer-tour-v1';
    const visible = (el) => (el && el.offsetParent !== null ? el : null);
    const TOUR = [
      { title: 'Choose your design', sel: () => visible(root.querySelector('[data-sheet="design"]')) || (root.querySelector('.cc-panel [data-act="type"]') && root.querySelector('.cc-panel [data-act="type"]').closest('.cc-seg')),
        text: () => `${visible(root.querySelector('[data-sheet="design"]')) ? 'Tap <strong>Design</strong> to pick' : 'Pick'} what to engrave: upload your <strong>Logo</strong>, type your own <strong>Text</strong>${p.areas.some((a) => a.art) ? ', or choose one of our <strong>artwork</strong> designs' : ''}. ${multi ? 'Select an outlined area on the preview to design each one.' : ''}` },
      { title: 'Position it', sel: () => stage,
        text: () => 'Drag your design on the preview to move it. Drag a corner to resize it, or the round handle to rotate it. The dashed line shows where we can engrave.' },
      { title: 'Pick your colors', sel: () => visible(root.querySelector('[data-sheet="color"]')) || root.querySelector('.cc-panel .cc-swatches'),
        text: () => `${visible(root.querySelector('[data-sheet="color"]')) ? 'Tap <strong>Color</strong> to choose' : 'Choose'} your ${esc(p.title)} color${visible(root.querySelector('[data-sheet="color"]')) ? '' : ' here'}. On the next step you can add more colors to your order, and every unit counts toward your ${DEALER ? 'dealer minimum' : 'bulk discount'}.` },
      { title: 'Approve your proof', sel: () => root.querySelector('.cc-nav [data-act="next"]'),
        text: () => "When it looks right, review your proof. Check spelling, size and placement, approve it, and add it to your cart. We engrave exactly what you approve." },
    ];
    const tourBox = root.querySelector('.cc-tour'), tourRing = root.querySelector('.cc-tour-ring');
    const tour = { i: -1 };
    const seenTour = () => { try { return localStorage.getItem(TOUR_KEY) === '1'; } catch { return false; } };
    const markTour = () => { try { localStorage.setItem(TOUR_KEY, '1'); } catch { /* private browsing: just show again next time */ } };
    function startTour(force) {
      if (cfg.tour === false || (!force && seenTour())) return;
      if (S.step !== 1) { S.step = 1; render(); }
      root.classList.add('cc-touring'); // extra scroll room so every target can be brought clear of the screen edges
      tour.i = 0; showTourStep();
    }
    function endTour() {
      tour.i = -1; tourBox.hidden = true; tourRing.hidden = true; markTour();
      root.classList.remove('cc-touring');
      const again = root.querySelector('.cc-tour-again'); if (again) again.focus();
    }
    function showTourStep() {
      const st = TOUR[tour.i], el = st && st.sel();
      if (!st) return endTour();
      if (!el) { tour.i++; return showTourStep(); } // nothing to point at (e.g. no colors): skip
      const last = tour.i === TOUR.length - 1;
      tourBox.innerHTML = `
        <p class="cc-tour-count">${tour.i + 1} of ${TOUR.length}</p>
        <p class="cc-tour-title" id="cc-tour-title">${esc(st.title)}</p>
        <p class="cc-tour-text">${st.text()}</p>
        <div class="cc-tour-actions">
          <button type="button" class="cc-tour-skip" data-tour="skip">${last ? '' : 'Skip'}</button>
          <span>
            ${tour.i ? '<button type="button" class="cc-tour-btn cc-tour-btn2" data-tour="back">Back</button>' : ''}
            <button type="button" class="cc-tour-btn" data-tour="next">${last ? 'Got it' : 'Next'}</button>
          </span>
        </div>
        <span class="cc-tour-arrow"></span>`;
      tourBox.hidden = false; tourRing.hidden = false;
      const wrap = root.querySelector('.cc-stage-wrap');
      if (phone() && wrap && wrap.contains(el)) {
        // Phones: the target is in the pinned preview area. Scrolling the page under it while the highlight
        // moves can leave iPhone Safari half-redrawn and unresponsive, so go to the top once and keep still;
        // the box sits just below the preview.
        if (window.scrollY > 0) window.scrollTo(0, 0);
      } else if (phone()) {
        // one instant jump, putting the target in the lower part of the visible screen
        const r = el.getBoundingClientRect(), v = visibleBox();
        window.scrollBy(0, Math.round(r.top - (v.top + v.height * 0.62)));
      } else {
        el.scrollIntoView({ block: 'center', behavior: 'auto' });
        fitTourTarget(el);
      }
      placeTour();
      const nx = tourBox.querySelector('[data-tour="next"]'); if (nx) nx.focus({ preventScroll: true });
    }
    // If the box can't fit either above or below its target (a tall preview on a small phone), scroll the target
    // up to the top of the screen; a pinned preview then stays there and the box gets the space below it.
    function fitTourTarget(el) {
      const r = el.getBoundingClientRect(), vh = window.innerHeight, gap = 14, bh = tourBox.offsetHeight;
      const fitsBelow = r.bottom + gap + bh <= vh - 8, fitsAbove = r.top - gap - bh >= 8;
      const vw = document.documentElement.clientWidth, bw = Math.min(340, vw - 24);
      const fitsSide = vw - r.right - gap >= bw + 12 || r.left - gap >= bw + 12;
      if (!fitsBelow && !fitsAbove && !fitsSide) window.scrollBy(0, r.top - 12);
      else if (r.bottom > vh - 12) window.scrollBy(0, r.bottom - vh + 12 + (fitsAbove ? 0 : bh + gap));
      // on phones the preview is pinned at the top: a target elsewhere must not end up hidden behind it
      const wrap = root.querySelector('.cc-stage-wrap');
      if (wrap && !wrap.contains(el) && getComputedStyle(wrap).position === 'sticky') {
        const wb = wrap.getBoundingClientRect().bottom, r2 = el.getBoundingClientRect();
        if (r2.top < wb + 8) window.scrollBy(0, r2.top - wb - 12);
      }
    }
    // Place the box next to its target: below it if there's room, otherwise above, kept on screen.
    function placeTour() {
      if (tour.i < 0) return;
      const el = TOUR[tour.i].sel(); if (!el) return;
      const r = el.getBoundingClientRect(), pad = 6;
      Object.assign(tourRing.style, { left: `${r.left - pad}px`, top: `${r.top - pad}px`, width: `${r.width + pad * 2}px`, height: `${r.height + pad * 2}px` });
      const vis = visibleBox(), vw = document.documentElement.clientWidth, vh = Math.min(window.innerHeight, vis.top + vis.height), gap = 14;
      const bw = Math.min(340, vw - 24); tourBox.style.width = `${bw}px`;
      const bh = tourBox.offsetHeight;
      const fitsBelow = r.bottom + gap + bh <= vh - 8, fitsAbove = r.top - gap - bh >= 8;
      const roomRight = vw - r.right - gap - 12, roomLeft = r.left - gap - 12;
      const arrow = tourBox.querySelector('.cc-tour-arrow');
      tourBox.classList.remove('is-above', 'is-right', 'is-left');
      if (!fitsBelow && !fitsAbove && (roomRight >= bw || roomLeft >= bw)) {
        // no room above or below (a tall preview): put the box beside it, level with the target's middle
        const right = roomRight >= bw;
        const cy = Math.max(r.top, 0) / 2 + Math.min(r.bottom, vh) / 2;
        const top = Math.max(8, Math.min(vh - bh - 8, cy - bh / 2));
        Object.assign(tourBox.style, { left: `${right ? r.right + gap : r.left - gap - bw}px`, top: `${top}px` });
        tourBox.classList.add(right ? 'is-right' : 'is-left');
        if (arrow) { arrow.style.left = ''; arrow.style.top = `${Math.max(18, Math.min(bh - 18, cy - top))}px`; }
        return;
      }
      const below = fitsBelow || !fitsAbove;
      const top = below ? Math.min(r.bottom + gap, vh - bh - 8) : r.top - gap - bh;
      const cx = r.left + r.width / 2, left = Math.max(12, Math.min(vw - bw - 12, cx - bw / 2));
      Object.assign(tourBox.style, { left: `${left}px`, top: `${Math.max(8, top)}px` });
      tourBox.classList.toggle('is-above', !below);
      if (arrow) { arrow.style.top = ''; arrow.style.left = `${Math.max(18, Math.min(bw - 18, cx - left))}px`; }
    }
    tourBox.addEventListener('click', (e) => {
      const b = e.target.closest('[data-tour]'); if (!b) return;
      if (b.dataset.tour === 'skip') return endTour();
      if (b.dataset.tour === 'back') { tour.i = Math.max(0, tour.i - 1); return showTourStep(); }
      tour.i++; showTourStep();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      if (tour.i >= 0) endTour(); else if (S.sheet) closeSheet();
    });
    let tourFrame = 0;
    const scheduleTour = () => { if (tour.i < 0 || tourFrame) return; tourFrame = requestAnimationFrame(() => { tourFrame = 0; placeTour(); }); };
    window.addEventListener('resize', scheduleTour);
    window.addEventListener('scroll', scheduleTour, { passive: true });

    /* ---------- boot ---------- */
    render();
    setTimeout(loadRepMode, 0); // after the rest of this setup (the staff-ordering helpers below are defined by then)
    setTimeout(() => { if (!REP) startTour(false); }, 700); // first visit: show the guided tour once the page has settled

    /* ---------- assisted orders: the staff bar, who it's for, and the order panel ---------- */
    const ss = {
      get: (k) => { try { return sessionStorage.getItem(k) || ''; } catch { return ''; } },
      set: (k, v) => { try { if (v) sessionStorage.setItem(k, v); else sessionStorage.removeItem(k); } catch { /* private browsing */ } },
    };
    p.variants.forEach((v) => { v._retail = v.price; });
    const retail = { fee: p.extraAreaPrice, currency: cfg.currency, dealer: DEALER, min: DEALER_MIN };
    const repMoney = (m) => (m ? new Intl.NumberFormat(undefined, { style: 'currency', currency: m.currencyCode || 'USD' }).format(Number(m.amount)) : '');
    const companyOf = (t) => RS.me && RS.me.companies.find((x) => x.target === t);
    function markInProgress(on) {
      const c = companyOf(RS.target);
      if (c) c.inProgress = on;
      else if (RS.me) RS.me.inProgress = on ? [...new Set(RS.me.inProgress.concat(RS.target))] : RS.me.inProgress.filter((x) => x !== RS.target);
    }
    let repBar = null, repDlg = null, findTimer = 0, customerHits = [];
    async function repPost(action, body) {
      const r = await fetch(libraryBase() + '/rep/' + action, { method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ target: RS.target, ...body }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || 'order ' + r.status);
      return j;
    }
    const repGet = (pathAndQuery) => fetch(libraryBase() + '/rep/' + pathAndQuery, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then((r) => r.json().then((j) => { if (!r.ok) throw new Error(j.error || 'request ' + r.status); return j; }));

    function loadRepMode() {
      if (CZ.customerLoggedIn === false || !libraryBase()) return;
      repGet('me').then((j) => {
        if (!j || !j.rep) return;
        REP = true; RS.me = j; RS.me.inProgress = j.inProgress || [];
        RS.target = ss.get('cling-rep-target'); RS.label = ss.get('cling-rep-label'); RS.contact = ss.get('cling-rep-contact');
        if (RS.target.startsWith('L') && !companyOf(RS.target)) RS.target = '';
        if (RS.target.startsWith('C') && !j.rep.all) RS.target = '';
        repBar = document.createElement('div');
        repBar.className = 'cc-rep'; repBar.setAttribute('role', 'region'); repBar.setAttribute('aria-label', 'Staff order');
        root.parentNode.insertBefore(repBar, root);
        repBar.addEventListener('input', (e) => { if (e.target.dataset.rep === 'find') findTargets(e.target.value); });
        repBar.addEventListener('focusin', (e) => { if (e.target.dataset.rep === 'find') { e.target.select(); findTargets(''); } });
        repBar.addEventListener('focusout', () => setTimeout(() => { if (!repBar.contains(document.activeElement)) closeResults(); }, 150));
        repBar.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeResults(); });
        repBar.addEventListener('change', (e) => { if (e.target.dataset.rep === 'contact') { RS.contact = e.target.value; ss.set('cling-rep-contact', RS.contact); } });
        repBar.addEventListener('click', (e) => {
          const pick = e.target.closest('[data-rep-pick]');
          if (pick) { closeResults(); chooseTarget(pick.dataset.repPick, pick.dataset.label); return; }
          if (e.target.closest('[data-rep="view"]')) { RS.note = ''; openRepPanel(); }
        });
        if (RS.target) chooseTarget(RS.target, RS.label); else { renderRepBar(); render(); }
      }).catch((e) => console.warn('Staff ordering unavailable; the customizer works as usual.', e)); // not staff, or the server is down
    }
    function renderRepBar() {
      if (!repBar) return;
      const c = RS.ctx && RS.ctx.kind === 'company' ? companyOf(RS.target) : null, contacts = c ? c.contacts : [];
      const locked = !!(RS.order && RS.order.lines.length && RS.order.contact);
      if (locked) RS.contact = RS.order.contact;
      if (c && !contacts.some((x) => x.id === RS.contact)) RS.contact = (contacts[0] || {}).id || '';
      const units = RS.order ? RS.order.units : 0, all = RS.me.rep.all;
      repBar.innerHTML = `<b>${all ? 'Staff order' : 'Sales rep'}: ${esc(RS.me.rep.name)}</b>
        <span class="cc-rep-find"><label>Ordering for <input type="search" data-rep="find" autocomplete="off" value="${esc(RS.label)}"
          placeholder="${all ? 'Find a company or customer' : 'Find one of your companies'}" aria-label="Who the order is for"></label>
          <span class="cc-rep-results" hidden></span></span>
        ${c ? `<label>Contact <select data-rep="contact" ${locked ? 'disabled title="Set for the order in progress"' : ''}>${contacts.length
            ? contacts.map((x) => `<option value="${esc(x.id)}" ${x.id === RS.contact ? 'selected' : ''}>${esc(x.name || x.email)}</option>`).join('')
            : '<option value="">No contacts in Shopify</option>'}</select></label>` : ''}
        ${RS.ctx ? `<span class="cc-rep-terms">${RS.ctx.kind === 'customer' ? `Customer${RS.ctx.address ? ` · ${esc(RS.ctx.address)}` : ''} · ` : ''}${esc(RS.ctx.terms)}</span>
          <button type="button" class="cc-rep-view" data-rep="view">Order: ${RS.order && RS.order.lines.length ? (DEALER ? `${units} customized unit${units === 1 ? '' : 's'}` : `${RS.order.lines.length} line${RS.order.lines.length === 1 ? '' : 's'}`) : 'empty'}</button>`
          : `<span>${all ? 'Search for the company or customer you\'re ordering for.' : 'Choose the company you\'re ordering for.'}</span>`}`;
    }
    const closeResults = () => { const box = repBar && repBar.querySelector('.cc-rep-results'); if (box) box.hidden = true; };
    function findTargets(q) {
      const box = repBar.querySelector('.cc-rep-results'), term = q.trim().toLowerCase();
      const comps = RS.me.companies.filter((c) => !term || (c.company + ' ' + c.location).toLowerCase().includes(term))
        .sort((a, b) => (b.inProgress - a.inProgress)).slice(0, term ? 8 : 6);
      const show = () => {
        const custs = term.length >= 2 ? customerHits : RS.me.inProgress.filter((t) => t.startsWith('C')).map((t) => ({ target: t, name: t === RS.target ? RS.label : 'Customer order in progress', email: '' }));
        box.innerHTML = comps.map((c) => `<button type="button" data-rep-pick="${esc(c.target)}" data-label="${esc(c.company)}"><b>${esc(c.company)}</b>${c.location ? ` · ${esc(c.location)}` : ''}
            <span>Company · ${esc(c.terms)}${c.inProgress ? ' · order in progress' : ''}</span></button>`).join('')
          + custs.map((c) => `<button type="button" data-rep-pick="${esc(c.target)}" data-label="${esc(c.name)}"><b>${esc(c.name)}</b>${c.email ? ` · ${esc(c.email)}` : ''}
            <span>Customer${c.place ? ` · ${esc(c.place)}` : ''}${RS.me.inProgress.includes(c.target) ? ' · order in progress' : ''}</span></button>`).join('')
          || `<span class="cc-rep-none">${term.length < 2 && RS.me.rep.all ? 'Type a name or email to search customers.' : 'No matches.'}</span>`;
        box.hidden = false;
      };
      customerHits = [];
      show();
      clearTimeout(findTimer);
      if (RS.me.rep.all && term.length >= 2) {
        findTimer = setTimeout(() => repGet('customers?q=' + encodeURIComponent(term))
          .then((j) => { customerHits = j.customers || []; if (repBar.querySelector('[data-rep="find"]').value.trim().toLowerCase() === term) show(); })
          .catch(() => {}), 250);
      }
    }
    function useRetail() {
      p.variants.forEach((v) => { v.price = v._retail; }); p.extraAreaPrice = retail.fee; cfg.currency = retail.currency;
      DEALER = retail.dealer; DEALER_MIN = retail.min; TIERS = DEALER ? [] : BASE_TIERS;
    }
    function chooseTarget(target, label) {
      RS.target = target; RS.label = label || ''; RS.ctx = null; RS.order = null; RS.rates = null; RS.note = ''; RS.invoice = false;
      ss.set('cling-rep-target', target); ss.set('cling-rep-label', RS.label);
      S.library = null; // the saved artwork reloads for this company
      useRetail(); cartUnits = 0;
      if (!target) { renderRepBar(); render(); return; }
      renderRepBar();
      const ids = p.variants.map((v) => v.id).concat(p.extraAreaVariantId ? [p.extraAreaVariantId] : []).join(',');
      repGet(`context?target=${encodeURIComponent(target)}&variants=${encodeURIComponent(ids)}`)
        .then((j) => {
          if (RS.target !== target) return; // another choice was made meanwhile
          RS.ctx = j; RS.order = j.order; RS.label = j.name; ss.set('cling-rep-label', j.name);
          // the target's own prices from Shopify, never the page's (a staff member logged in as a dealer sees dealer
          // prices on the page): a company's catalog prices, or a consumer's retail prices in their market
          let currency = null;
          p.variants.forEach((v) => { const pr = j.prices[String(v.id)]; v.price = pr ? Math.round(Number(pr.amount) * 100) : v._retail; if (pr) currency = pr.currencyCode; });
          const fee = p.extraAreaVariantId && j.prices[String(p.extraAreaVariantId)];
          p.extraAreaPrice = fee ? Math.round(Number(fee.amount) * 100) : retail.fee;
          cfg.currency = currency || retail.currency;
          if (j.dealer) { DEALER = true; DEALER_MIN = j.dealerMin; TIERS = []; } // a company: no volume tiers, the dealer minimum
          else { DEALER = false; DEALER_MIN = 0; TIERS = BASE_TIERS; } // a consumer: the volume discounts apply to the order too
          cartUnits = DEALER ? RS.order.units : 0; S.stepError = '';
          renderRepBar(); render();
        })
        .catch((e) => { S.stepError = e.message || 'That couldn\'t be loaded.'; RS.target = ''; ss.set('cling-rep-target', ''); renderRepBar(); render(); });
    }
    function openRepPanel() {
      if (!repDlg) {
        repDlg = document.createElement('dialog');
        repDlg.className = 'cc-rep-dialog';
        repDlg.addEventListener('close', () => { if (S.repAdded) global.location.reload(); }); // after an add: a fresh design
        document.body.appendChild(repDlg);
        repDlg.addEventListener('change', async (e) => {
          if (e.target.dataset.repInvoice !== undefined) { RS.invoice = e.target.checked; return; }
          if (e.target.dataset.repShip === undefined || !e.target.value) return;
          RS.busy = true; RS.note = ''; renderRepPanel();
          try { RS.order = (await repPost('ship', { rate: e.target.value })).order; } catch (err) { RS.note = err.message; }
          RS.busy = false; renderRepPanel();
        });
        repDlg.addEventListener('click', async (e) => {
          const b = e.target.closest('button'); if (!b || RS.busy) return;
          if (b.dataset.repAct === 'close') return repDlg.close();
          const act = b.dataset.repAct, name = RS.ctx ? RS.ctx.name : '';
          if (act === 'place' && !confirm(`Place this order for ${name}? It goes to the engraving dashboard right away${RS.ctx && RS.ctx.kind === 'customer' ? ', and the customer is emailed an invoice to pay' : ', and the company pays on its terms'}.`)) return;
          if (act === 'discard' && !confirm(`Discard the order in progress for ${name}?`)) return;
          RS.busy = true; RS.note = ''; renderRepPanel();
          try {
            const j = await repPost(act, act === 'remove' ? { line: b.dataset.line } : act === 'place' ? { invoice: RS.invoice } : {});
            if (act === 'place') {
              RS.order = { units: 0, lines: [], total: null, shipping: null, name: null };
              RS.note = `Order ${j.orderName} placed for ${j.name} (${j.terms}). It's on the engraving dashboard now.${j.invoiced ? ` An invoice was emailed${j.email ? ` to ${j.email}` : ''}.` : ''}`;
              markInProgress(false);
            } else {
              RS.order = j.order; RS.rates = null;
              markInProgress(!!RS.order.lines.length);
              RS.note = act === 'discard' ? 'Order discarded.' : 'Removed. Choose the shipping rate again, as it depends on what\'s in the order.';
            }
          } catch (err) { RS.note = err.message; }
          RS.busy = false; cartUnits = RS.order && DEALER ? RS.order.units : 0;
          renderRepPanel(); renderRepBar(); render();
        });
      }
      renderRepPanel();
      if (!repDlg.open) repDlg.showModal();
    }
    function loadRates() {
      if (RS.rates || RS.busy || !RS.order || !RS.order.lines.length) return;
      RS.rates = 'loading';
      repGet('shipping?target=' + encodeURIComponent(RS.target))
        .then((j) => { RS.rates = j.rates || []; })
        .catch((e) => { RS.rates = []; RS.note = e.message; })
        .then(() => { if (repDlg && repDlg.open) renderRepPanel(); });
    }
    function renderRepPanel() {
      const o = RS.order || { units: 0, lines: [] }, ctx = RS.ctx || {}, dealer = !!ctx.dealer;
      const short = dealer && o.units < DEALER_MIN;
      loadRates();
      const rates = Array.isArray(RS.rates) ? RS.rates : null, needShip = !o.shipping && !!(rates && rates.length);
      repDlg.innerHTML = `<div class="cc-rep-panel">
        <h2>${esc(ctx.name || '')}'s order${o.name ? ` <span>${esc(o.name)}</span>` : ''}</h2>
        ${RS.note ? `<p class="cc-rep-note" role="status">${esc(RS.note)}</p>` : ''}
        ${o.lines.length ? `<table>${o.lines.map((l) => `<tr>
            <td>${l.preview ? `<img src="${esc(l.preview)}" alt="">` : ''}</td>
            <td><b>${esc(l.title)}</b><div class="cc-rep-eng">${esc(l.engraving)}</div></td>
            <td>× ${l.quantity}</td><td>${repMoney(l.price)}</td>
            <td><button type="button" class="cc-rep-link" data-rep-act="remove" data-line="${esc(l.id)}">Remove</button></td></tr>`).join('')}
          </table>
          <label class="cc-rep-ship">Shipping ${rates === null ? '<span>Getting rates from Shopify…</span>' : rates.length
            ? `<select data-rep-ship ${RS.busy ? 'disabled' : ''}><option value="">Choose a shipping rate…</option>${rates.map((r) => `
                <option value="${esc(r.handle)}" ${o.shipping && o.shipping.title === r.title ? 'selected' : ''}>${esc(r.title)}, ${repMoney(r.price)}</option>`).join('')}</select>`
            : '<span>No shipping rates for this address in Shopify. Add shipping in Shopify after placing, if needed.</span>'}</label>
          <p class="cc-rep-total">Total ${repMoney(o.total)} <span>${o.shipping ? `including ${esc(o.shipping.title)}` : 'before shipping'}, before tax${dealer ? ` · ${o.units} customized unit${o.units === 1 ? '' : 's'}` : ''}</span></p>
          ${short ? `<p class="cc-dealer-status">Dealer orders need at least ${DEALER_MIN} customized units: add ${DEALER_MIN - o.units} more to place this order.</p>` : ''}
          ${ctx.kind === 'customer' ? `<p class="cc-rep-eng">When placed, ${esc(ctx.email || 'the customer')} is emailed an invoice with a payment link.</p>`
            : `<label class="cc-rep-inv"><input type="checkbox" data-rep-invoice ${RS.invoice ? 'checked' : ''}> Email an invoice to the company contact now (otherwise they pay on their terms)</label>`}`
          : '<p>Nothing in this order yet. Design an engraving, approve the proof, and add it.</p>'}
        <div class="cc-rep-actions">
          ${o.lines.length ? `<button type="button" class="cc-btn cc-btn-primary" data-rep-act="place" ${short || needShip || RS.busy || rates === null ? 'disabled' : ''}
            title="${short ? 'Below the dealer minimum' : needShip ? 'Choose a shipping rate first' : ''}">${RS.busy ? 'Working…' : 'Place order'}</button>` : ''}
          <button type="button" class="cc-btn" data-rep-act="close">Keep designing</button>
          ${o.lines.length ? '<button type="button" class="cc-rep-link" data-rep-act="discard">Discard order</button>' : ''}
        </div></div>`;
    }    if (ART.length) {
      Promise.all(ART.map((it) => loadImg(it.url).then((img) => {
        if (img) it.src = E.sourceFromImage(img, 2000); else it.failed = true;
      }))).then(() => { p.areas.forEach((a) => (S.designs[a.key]._cache = null)); render(); });
    }
    if (document.fonts && document.fonts.load) {
      Promise.all(E.FONTS.map((f) => document.fonts.load(`${f.weight} 40px "${f.id}"`).catch(() => null)))
        .then(() => { p.areas.forEach((a) => (S.designs[a.key]._cache = null)); if (S.step === 2) paintProofs(); draw(); renderLive(); });
    }
    return { state: S, render };
  }

  function parseHexMap(src) {
    const out = {};
    if (!src) return out;
    if (typeof src === 'object') { Object.keys(src).forEach((k) => (out[k.toLowerCase()] = src[k])); return out; }
    String(src).split(/\n|,/).forEach((line) => {
      const m = line.match(/^\s*(.+?)\s*[:=]\s*(#[0-9a-f]{3,6})\s*$/i);
      if (m) out[m[1].toLowerCase()] = m[2];
    });
    return out;
  }

  // Guess which render a Shopify product uses from its handle or title.
  function detectRender(handle, title) {
    const s = `${handle || ''} ${title || ''}`.toLowerCase();
    if (/double\s*-?\s*stack/.test(s)) return 'doublestack';
    if (/boat\s*-?\s*bar/.test(s)) return 'boat-bar';
    if (/delta/.test(s)) return 'delta';
    if (/\bmini\b/.test(s)) return 'mini';
    if (/\bplus\b/.test(s)) return 'plus';
    return null;
  }

  global.ClingCustomizer = { mount, RENDERS, detectRender };
})(window);
